import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useCountUp from '../hooks/useCountUp';
import { api } from '../services/api';
import { localDateStr, contactAge } from '../utils/dateUtils';
import { smsInfo } from '../utils/sms';
import SectionHint from '../components/SectionHint';
import { fr } from 'date-fns/locale';
import { useApp } from '../context/AppContext';
import { useContactPreview } from '../context/ContactPreviewContext';
import { SMS_PACKS, SMS_LOW_THRESHOLD, recommendedPack } from '../data/smsPacks';
import { MARRONNIERS, MARRONNIER_BODIES } from '../data/marronniers';
import { Card, CardHeader, StatCard } from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge, { ScoreBadge } from '../components/UI/Badge';
import { format } from 'date-fns';
import { getAvatarStyle } from '../utils/avatar';
import { StrategicDashboard } from './Strategy';
import OpportunityRadar from '../components/OpportunityRadar';
import ProgressRoadmap from '../components/ProgressRoadmap';
import GenieJournal from '../components/GenieJournal';
import { ASTUCES } from '../data/astuces';
import mascotAiImg from '../assets/genie-appee.png';
import { getFallbackDashboardInsight } from '../utils/aiContext';
import './Dashboard.css';

// ── Hooks d'animation ────────────────────────────────────────────────────────

function useTypewriter(text, speed = 18, delay = 1000) {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    if (!text) return;
    setDisplayed('');
    let i = 0;
    const timer = setTimeout(() => {
      const iv = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) clearInterval(iv);
      }, speed);
      return () => clearInterval(iv);
    }, delay);
    return () => clearTimeout(timer);
  }, [text, speed, delay]);
  return displayed || '';
}

// ── Phrases positives pool (section 31 CDC) ───────────────────────────────────
const POSITIVE_PHRASES = [
  "J'ai passé en revue ta base ce matin — tout est nickel, tu peux souffler 😌",
  "Pendant que tu bosses, je garde un œil sur toutes tes relations. C'est mon job 💙",
  "Tes clients ne savent pas qu'Appee existe — ils voient juste que tu penses à eux. Et c'est tout ce qui compte.",
  "Un anniversaire bien soigné vaut mieux qu'une campagne pub. Je m'en charge, toi tu en récoltes les fruits.",
  "Tu n'as rien à oublier — j'y pense avant toi. C'est exactement pour ça que je suis là.",
  "Chaque attention que j'envoie en ton nom tisse un lien un peu plus solide. On construit quelque chose.",
  "Les meilleurs clients ne cherchent pas le prix. Ils cherchent à se sentir reconnus. C'est ce qu'on fait ensemble.",
  "La régularité construit la fidélité. Je veille à ce que tu sois toujours là au bon moment.",
  "Les meilleures relations se nourrissent d'attentions simples. Je les prépare, toi tu les signes ✍️",
  "Je surveille tes contacts pendant que tu gères ton activité. On forme une belle équipe 🤝",
];

function getDailyPhrase() {
  const idx = new Date().getDate() % POSITIVE_PHRASES.length;
  return POSITIVE_PHRASES[idx];
}

// ── Appee Coach: strategy tracking ────────────────────────────────────────────

function getDurationDays(dur = '') {
  const n = parseInt(dur) || 0;
  if (dur.includes('12 mois')) return 365;
  if (dur.includes('mois'))    return n * 30;
  if (dur.includes('semaine')) return n * 7;
  return 90;
}

function getStratStatus(strat) {
  const phases = strat.phases || [];
  const total  = phases.reduce((s, p) => s + (p.actions?.length || 0), 0);
  const done   = phases.reduce((s, p) => s + (p.actions?.filter(a => a.done).length || 0), 0);
  const pct    = total > 0 ? Math.round(done / total * 100) : 0;
  const launchedDays = Math.floor((Date.now() - new Date(strat.launchedAt).getTime()) / 86400000);
  const totalDays    = getDurationDays(strat.duration || '');
  const expectedPct  = Math.min(100, Math.round((launchedDays / Math.max(totalDays, 1)) * 100));
  const isBehind     = pct < expectedPct - 12 && launchedDays > 3;
  const curPhase     = phases[strat.currentPhase || 0] || phases[0];
  const nextTasks    = (curPhase?.actions || []).filter(a => !a.done).slice(0, 3);
  return { pct, total, done, launchedDays, isBehind, nextTasks, curPhase };
}

function getCoachMessage(pct, isBehind, launchedDays) {
  if (isBehind) return { text: `Tu es en retard de ${launchedDays} jours sur le planning prévu. Il faut reprendre le rythme cette semaine — voici les actions prioritaires :`, warn: true };
  if (pct === 0) return { text: `Stratégie lancée — fais ta première action aujourd'hui pour prendre de l'élan ! Je suis là pour t'aider.`, warn: false };
  if (pct >= 100) return { text: `Stratégie terminée à 100% 🎉 C'est une vraie performance. Prêt pour la prochaine étape ?`, warn: false };
  if (pct < 30)  return { text: `Belle entrée en matière à ${pct}% ! Les premières semaines donnent le ton — continue sur cette lancée.`, warn: false };
  if (pct < 70)  return { text: `${pct}% d'avancement — tu es en bonne voie. La régularité fait toute la différence, je suis là pour toi.`, warn: false };
  return { text: `${pct}% — impressionnant ! Tu es presque au bout. Maintiens ce rythme sur les dernières actions.`, warn: false };
}

const CH_ICONS = { email:'📧', sms:'📱', social:'📲', print:'🖨️', event:'🎉', landing:'🌐', phone:'📞', instore:'🏪' };

function AppeeCoachWidget({ navigate, bdayData }) {
  const { strategyInstances: strategies } = useApp();
  const hasStrat = strategies.length > 0;

  // Empty state — CTA to create strategy
  if (!hasStrat) {
    return (
      <div className="acw-empty-cta">
        <div className="acw-empty-left">
          <div className="acw-empty-icon">🚀</div>
          <div>
            <div className="acw-empty-title">Développe tes affaires avec Appee</div>
            <div className="acw-empty-sub">Lance ta première stratégie — je te guide de A à Z et je fais le maximum à ta place.</div>
          </div>
        </div>
        <button className="acw-empty-btn" onClick={() => navigate('/strategy')}>Créer ma stratégie →</button>
      </div>
    );
  }

  return (
    <div className="acw-duo">

      {/* ── Calendrier Marketing column (left) ── */}
      <MarronniersCell />

      {/* ── Strategies column ── */}
      <div className="acw-cell">
        <div className="acw-cell-head">
          <span className="acw-cell-title">🧠 Stratégies en cours</span>
          <button className="acw-cell-link" onClick={() => navigate('/strategy')}>
            {hasStrat ? 'Voir tout →' : 'Créer →'}
          </button>
        </div>
        {hasStrat ? strategies.slice(0, 3).map(s => {
          const { pct, isBehind } = getStratStatus(s);
          return (
            <button key={s.instanceId} className="acw-row" onClick={() => navigate('/strategy')}>
              <span className="acw-row-icon">{s.icon}</span>
              <div className="acw-row-body">
                <div className="acw-row-name">{s.title}</div>
                <div className="acw-bar-wrap">
                  <div className="acw-bar-fill" style={{ width: `${pct}%`, background: isBehind ? '#EF4444' : pct >= 70 ? '#10B981' : '#5A35FF' }} />
                </div>
              </div>
              <span className="acw-row-pct" style={{ color: isBehind ? '#EF4444' : pct >= 70 ? '#10B981' : '#5A35FF' }}>{pct}%</span>
            </button>
          );
        }) : (
          <div className="acw-cell-empty">
            Aucune stratégie active
            <button onClick={() => navigate('/strategy')}>Lancer →</button>
          </div>
        )}
      </div>


    </div>
  );
}

// ── Marketing Marronniers ────────────────────────────────────────────────────

function getUpcomingMarronniers(daysAhead = 90) {
  const now = new Date();
  const year = now.getFullYear();
  const results = [];
  for (const m of MARRONNIERS) {
    let d = new Date(year, m.month - 1, m.day);
    // Handle approximate dates (Fête des Mères = 2nd Sunday of May, Fête des Pères = 3rd Sunday of June)
    if (d < now) d = new Date(year + 1, m.month - 1, m.day);
    const diffDays = Math.ceil((d - now) / 86400000);
    if (diffDays >= 0 && diffDays <= daysAhead) {
      results.push({ ...m, date: d, diffDays });
    }
  }
  return results.sort((a, b) => a.diffDays - b.diffDays);
}

function MarronniersCell() {
  const upcoming = getUpcomingMarronniers(90);
  if (upcoming.length === 0) return null;
  const next = upcoming[0];
  const rest = upcoming.slice(1, 3);

  const daysLabel = next.diffDays === 0
    ? "C'est aujourd'hui !"
    : next.diffDays === 1
    ? "C'est demain !"
    : `dans ${next.diffDays} jour${next.diffDays > 1 ? 's' : ''}`;

  const dayAbbr = format(next.date, 'EEE', { locale: fr }).toUpperCase().slice(0, 3);
  const dayNum  = format(next.date, 'd');

  return (
    <div className="acw-cell acw-cell-marronnier">
      <div className="acw-cell-head">
        <span className="acw-cell-title" style={{ color: '#EA580C' }}>📅 Calendrier marketing</span>
      </div>
      <div className="acw-marr-next">
        <div className="acw-marr-date">
          <span className="acw-marr-date-abbr">{dayAbbr}</span>
          <span className="acw-marr-date-num">{dayNum}</span>
        </div>
        <div className="acw-marr-info">
          <div className="acw-marr-name">
            {next.name} — <span className="acw-marr-days">{daysLabel}</span>
          </div>
          <div className="acw-marr-tip">
            {next.diffDays <= 7 ? "Plus vraiment le temps de traîner — " : ""}Pense à préparer {next.campaign} !{' '}
            {next.diffDays > 14 ? '😉' : next.diffDays > 7 ? '🚀' : '⚡'}
          </div>
          <Link to="/campaigns/new">
            <button className="acw-marr-cta">Créer la campagne →</button>
          </Link>
        </div>
      </div>
      {rest.length > 0 && (
        <div className="acw-marr-rest">
          {rest.map(m => (
            <div key={`${m.month}-${m.day}`} className="acw-marr-rest-item">
              <div className="acw-marr-rest-name">{m.icon} {m.name}</div>
              <div className="acw-marr-rest-days">dans {m.diffDays}j</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Company anniversary helper ────────────────────────────────────────────────
function getCompanyAnniversary(cp = {}) {
  if (!cp.founded) return null;
  const founded = new Date(cp.founded);
  const now = new Date();
  const nextBday = new Date(now.getFullYear(), founded.getMonth(), founded.getDate());
  if (nextBday < now) nextBday.setFullYear(now.getFullYear() + 1);
  const daysUntil = Math.ceil((nextBday - now) / 86400000);
  if (daysUntil > 60) return null;
  const years = nextBday.getFullYear() - founded.getFullYear();
  return { daysUntil, years, name: cp.name || 'l\'entreprise', isMilestone: years % 5 === 0 };
}

// ── Appee voice greeting ──────────────────────────────────────────────────────
function buildAppeeGreeting(todayBdayCount, dormant, openRate, vip, cp = {}, totalContacts = null) {
  // Base vide : pas de « j'ai passé en revue ta base » mensonger — on dit la
  // vérité et on pointe la prochaine étape.
  if (totalContacts === 0) {
    return `Ta base est toute neuve — importe tes premiers clients et je me mets au travail : anniversaires, relances, opportunités. Je suis prêt 🧞`;
  }
  const parts = [];
  const ann = getCompanyAnniversary(cp);

  // Anniversaire entreprise — priorité absolue
  if (ann) {
    if (ann.daysUntil === 0) {
      parts.push(ann.isMilestone
        ? `🎂 C'est aujourd'hui — ${ann.name} fête ses ${ann.years} ans ! C'est un moment fort — dis-moi si tu veux qu'on marque le coup avec tes clients 🎉`
        : `🎂 Aujourd'hui, ${ann.name} a ${ann.years} ans ! Belle journée pour célébrer ça avec tes clients.`
      );
    } else if (ann.daysUntil <= 7) {
      parts.push(ann.isMilestone
        ? `🎂 Dans ${ann.daysUntil} jours, ${ann.name} fête ses ${ann.years} ans — un anniversaire rond, ça se prépare ! Je peux t'aider à créer un événement mémorable.`
        : `🎂 L'anniversaire de ${ann.name} (${ann.years} ans) approche — dans ${ann.daysUntil} jours. Belle occasion de contacter tes clients fidèles !`
      );
    } else {
      parts.push(`🎂 Dans ${ann.daysUntil} jours, ${ann.name} fête ses ${ann.years} ans${ann.isMilestone ? ' 🎉 (un beau milestone !)' : ''}. J'ai noté ça dans mon agenda.`);
    }
  }

  if (todayBdayCount > 0) {
    parts.push(todayBdayCount === 1
      ? `J'ai repéré 1 anniversaire aujourd'hui — j'ai tout préparé, le message part ce matin à 7h. Toi, tu n'as rien à faire 🎂`
      : `J'ai repéré ${todayBdayCount} anniversaires aujourd'hui — les messages sont prêts et partiront ce matin à 7h. Profite, je m'en occupe 🎂`
    );
  }
  if (dormant > 5) {
    parts.push(`J'ai aussi repéré ${dormant} contacts silencieux depuis plus de 12 mois — je peux préparer une campagne "vous nous manquez" quand tu veux 💤`);
  } else if (vip > 0 && todayBdayCount === 0) {
    parts.push(`Ta base compte ${vip} client${vip > 1 ? 's' : ''} VIP — dis-moi et je prépare quelque chose de spécial pour eux ⭐`);
  }
  if (openRate >= 40) {
    parts.push(`${openRate} % de tes messages sont ouverts — c'est vraiment bien, on est sur la bonne voie 🚀`);
  }
  if (parts.length === 0) {
    const nomE = cp.name ? `chez ${cp.name}` : 'dans ton activité';
    return `J'ai passé en revue toute ta base ce matin — tout est tranquille ${nomE}, tu peux te concentrer sur ce qui compte. Je veille 👁️`;
  }
  return parts.join(' ');
}

function getAge(birthDate) {
  // Année sentinelle ≤1904 = « année inconnue » (jour/mois seul) → pas d'âge
  return contactAge(birthDate);
}

function computeOpportunities(contacts, dormantCount, vipCount) {
  const missingEmail = contacts.filter(c => !c.email && c.status === 'active').length;
  return [
    {
      id: 'vip', icon: '⭐', color: '#FFC400',
      bg: 'linear-gradient(135deg, #FFFBEB, #FEF3C7)', border: '#FCD34D',
      title: 'Programme VIP', desc: `${vipCount} clients VIP identifiés`,
      value: vipCount * 200, action: 'Créer programme VIP', link: '/strategy',
    },
    {
      id: 'reactivation', icon: '💤', color: '#5A35FF',
      bg: 'linear-gradient(135deg, #EDE9FF, #DDD6FE)', border: '#A78BFA',
      title: 'Réactiver les dormants', desc: `${dormantCount} clients sans interaction 12m+`,
      value: dormantCount * 50, action: 'Campagne "Vous nous manquez"', link: '/campaigns/new',
    },
    {
      id: 'email', icon: '📧', color: '#10B981',
      bg: 'linear-gradient(135deg, #ECFDF5, #D1FAE5)', border: '#6EE7B7',
      title: 'Collecter les e-mails', desc: `${missingEmail} clients actifs sans e-mail`,
      value: missingEmail * 30, action: 'Collecter avec incentive', link: '/contacts',
    },
  ].filter(o => o.value > 0);
}

// ── Pastille civilité §16.2 ───────────────────────────────────────────────────
const GENDER_BADGE = {
  monsieur: { label: 'M.',   bg: '#EFF6FF', color: '#2563EB' },
  madame:   { label: 'Mme',  bg: '#FDF2F8', color: '#DB2777' },
  autre:    { label: 'Autre',bg: '#F3F4F6', color: '#6B7280' },
};

function GenderPill({ gender }) {
  const g = GENDER_BADGE[gender];
  if (!g) return null;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, background: g.bg, color: g.color,
      border: `1px solid ${g.color}22`, lineHeight: '20px', flexShrink: 0,
    }}>{g.label}</span>
  );
}

// ── Statut email anniversaire (lecture seule, envoi automatique) ─────────────
const todayStr = localDateStr();

/** Extrait l'heure réelle d'envoi depuis un champ datetime (ex: "2025-05-16T07:03:14") */
function fmtSentTime(dt) {
  if (!dt) return null;
  try {
    const d = new Date(String(dt).replace(' ', 'T'));
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
  } catch { return null; }
}

/** Date anniversaire cette année pour un contact (YYYY-MM-DD) */
function bdayThisYear(contact) {
  if (!contact.birth_date) return null;
  return `${todayStr.slice(0, 4)}-${contact.birth_date.slice(5, 10)}`;
}

function BirthdayEmailStatus({ contact }) {
  const emailDate = contact.last_email_sent?.slice(0, 10);
  const openDate  = contact.last_email_opened?.slice(0, 10);
  const bday      = bdayThisYear(contact);
  const sentToday   = emailDate && (emailDate === todayStr || emailDate === bday);
  const openedToday = openDate  && (openDate  === todayStr || openDate  === bday);

  if (!contact.email && !contact.phone) {
    return (
      <div className="bday-auto-status bday-auto-none">
        <span className="bday-auto-icon">😶</span>
        <span className="bday-auto-text">Injoignable</span>
      </div>
    );
  }

  if (!contact.email) {
    return (
      <div className="bday-auto-status bday-auto-sms">
        <span className="bday-auto-icon">💬</span>
        <span className="bday-auto-text">Via SMS</span>
      </div>
    );
  }

  if (openedToday) {
    return (
      <div className="bday-auto-status bday-auto-read">
        <span className="bday-auto-icon">💛</span>
        <span className="bday-auto-text">Vu ! ✓</span>
      </div>
    );
  }

  if (sentToday) {
    const sentTime = fmtSentTime(contact.last_email_sent);
    return (
      <div className="bday-auto-status bday-auto-sent">
        <span className="bday-auto-icon">✅</span>
        <span className="bday-auto-text">Envoyé{sentTime ? ` à ${sentTime}` : ' ✓'}</span>
      </div>
    );
  }

  return (
    <div className="bday-auto-status bday-auto-pending">
      <span className="bday-auto-icon">🕐</span>
      <span className="bday-auto-text">Envoi à 7h00</span>
    </div>
  );
}

// ── Statut envoi automatique (email / SMS / injoignable) ─────────────────────
// Toujours affiché — montre que Appee gère tout automatiquement
function getAutoStatus(contact, isToday = false) {
  const hasEmail = !!contact.email;
  const hasPhone = !!contact.phone;
  const emailDate = contact.last_email_sent?.slice(0, 10);
  const openDate  = contact.last_email_opened?.slice(0, 10);
  const bday      = bdayThisYear(contact);
  const sentToday   = emailDate && (emailDate === todayStr || emailDate === bday);
  const openedToday = openDate  && (openDate  === todayStr || openDate  === bday);

  if (!hasEmail && !hasPhone) return { type: 'incomplete', label: 'Injoignable', icon: '😶', cls: 'week-badge-none', channel: 'none' };

  // Pas d'email → SMS uniquement
  if (!hasEmail) {
    const smsStatus = contact.birthday_sms_status ?? null;
    const smsSentAt = contact.birthday_sms_sent_at ?? null;
    if (smsStatus === 'sent') {
      const t = fmtSentTime(smsSentAt);
      return { type: 'sms', label: t ? `SMS à ${t}` : 'SMS envoyé', icon: '📱', cls: 'week-badge-sms', channel: 'sms' };
    }
    if (smsStatus === 'failed') return { type: 'failed', label: 'SMS échoué', icon: '📱', cls: 'week-badge-none', channel: 'sms' };
    return { type: 'sms', label: hasPhone ? 'SMS programmé' : 'Injoignable', icon: '📱', cls: 'week-badge-sms', channel: 'sms' };
  }

  const logStatus   = contact.birthday_email_status    ?? null;
  const logSentAt   = contact.birthday_email_sent_at   ?? null;
  const logOpenedAt = contact.birthday_email_opened_at ?? null;
  const logOpens    = contact.birthday_email_opens     ?? 0;

  if (logStatus === 'failed') return { type: 'failed', label: 'Échec envoi', icon: '📧', cls: 'week-badge-none', channel: 'email' };

  if (isToday) {
    if (logOpens > 0 || logStatus === 'opened' || openedToday) {
      const t = fmtSentTime(logOpenedAt || contact.last_email_opened);
      return { type: 'read', label: t ? `Lu à ${t}` : 'Lu', icon: '📧', cls: 'week-badge-read', channel: 'email' };
    }
    if (logStatus === 'sent' || sentToday) {
      const t = fmtSentTime(logSentAt || contact.last_email_sent);
      return { type: 'sent', label: t ? `Envoyé à ${t}` : 'Envoyé', icon: '📧', cls: 'week-badge-sent', channel: 'email' };
    }
    return { type: 'sending', label: 'Envoi à 7h00', icon: '📧', cls: 'week-badge-sending', channel: 'email' };
  }
  return { type: 'scheduled', label: 'Programmé', icon: '📧', cls: 'week-badge-scheduled', channel: 'email' };
}

// ── Bloc d'info unifié (canal + statuts + tags) ───────────────────────────────
const STATUS_STYLE = {
  read:      { dot: '#FBBF24', text: '#92400E' },
  sent:      { dot: '#34D399', text: '#065F46' },
  sending:   { dot: '#A78BFA', text: '#5B21B6' },
  scheduled: { dot: '#93C5FD', text: '#1E40AF' },
  sms:       { dot: '#34D399', text: '#065F46' },
  failed:    { dot: '#F87171', text: '#991B1B' },
  incomplete:{ dot: '#D1D5DB', text: '#6B7280' },
};

function SendChip({ icon, label, type }) {
  const s = STATUS_STYLE[type] || STATUS_STYLE.scheduled;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      border: '1px solid #E5E7EB', borderRadius: 20,
      padding: '3px 9px', fontSize: 12, fontWeight: 600,
      color: s.text, background: '#fff', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {icon} {label}
    </span>
  );
}

function InfoTag({ children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      border: '1px solid #E5E7EB', borderRadius: 20,
      padding: '2px 8px', fontSize: 11, fontWeight: 500,
      color: '#6B7280', background: '#F9FAFB', whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

// ── Recommandation appel (VIP ou âge rond) ────────────────────────────────────
// Affiché EN PLUS du statut automatique quand la relation mérite une attention humaine
function needsCallAdvice(contact) {
  const age = getAge(contact.birth_date);
  return contact.category === 'VIP' || (age !== null && age % 10 === 0);
}

// Rétrocompat — utilisé dans quelques endroits anciens
function getContactAppeeStatus(contact, isToday = false) {
  return getAutoStatus(contact, isToday);
}

// Fallback statique si l'IA n'a pas encore répondu
function getConseilFallback(contact) {
  const age = getAge(contact.birth_date);
  const isRound = age !== null && age % 10 === 0;
  const isVip = contact.category === 'VIP';
  if (isRound && isVip)
    return `${contact.first_name} fête ses ${age} ans — un cap symbolique — et fait partie de tes contacts importants. Un appel de 2 minutes aujourd'hui créera beaucoup plus de lien.`;
  if (isRound)
    return `${contact.first_name} fête ses ${age} ans. Un appel personnel ferait vraiment la différence pour un anniversaire aussi symbolique.`;
  if (isVip)
    return `${contact.first_name} est un contact important. Ta présence humaine aujourd'hui renforcerait vraiment la relation.`;
  return null;
}

// Conseil IA si disponible, sinon fallback statique
function getConseilMessage(contact) {
  return contact.ai_conseil || getConseilFallback(contact);
}

// ── Jauge anniversaires du mois ──────────────────────────────────────────────
function BirthdayMonthGauge({ today, week, month, sent }) {
  const now = new Date();
  const monthName = now.toLocaleDateString('fr-FR', { month: 'long' });
  const monthCap  = monthName.charAt(0).toUpperCase() + monthName.slice(1);
  const totalMonth = month > 0 ? month : 0;
  const sentSafe   = Math.min(sent, totalMonth);
  const pct        = totalMonth > 0 ? Math.round((sentSafe / totalMonth) * 100) : 0;
  const progressPct= totalMonth > 0 ? (sentSafe / totalMonth) * 100 : 0;

  return (
    <div className="bday-gauge-block">
      <div className="bday-gauge-top">
        <div className="bday-gauge-main">
          <div className="bday-gauge-header">
            <span className="bday-gauge-title">🎂 Anniversaires — {monthCap}</span>
            <Link to="/birthdays" className="bday-gauge-link">Voir tous →</Link>
          </div>
          <div className="bday-gauge-bar-wrap">
            <div className="bday-gauge-bar-track">
              <div className="bday-gauge-bar-fill" style={{ width: `${Math.min(progressPct, 100)}%` }} />
            </div>
            <div className="bday-gauge-bar-labels">
              <span>{sentSafe} envoyé{sentSafe > 1 ? 's' : ''}</span>
              <span className="bday-gauge-pct">{pct}%</span>
              <span>{totalMonth} ce mois</span>
            </div>
          </div>
        </div>
        <div className="bday-mini-stats">
          <Link to="/birthdays?tab=today" className="bday-mini-stat">
            <span className="bday-mini-val">{today}</span>
            <span className="bday-mini-label">Aujourd'hui</span>
          </Link>
          <div className="bday-mini-sep" />
          <Link to="/birthdays?tab=week" className="bday-mini-stat">
            <span className="bday-mini-val">{week}</span>
            <span className="bday-mini-label">Cette semaine</span>
          </Link>
          <div className="bday-mini-sep" />
          <Link to="/birthdays" className="bday-mini-stat">
            <span className="bday-mini-val">{totalMonth}</span>
            <span className="bday-mini-label">Ce mois</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Notification Bell ─────────────────────────────────────────────────────────
const NOTIF_DISMISSED_KEY = 'appee_notifs_dismissed_v2';

function notifId(n) {
  return `${n.type}::${String(n.text).slice(0, 50)}`;
}

function NotificationBell({ urgentItems, strategies, tenant, contacts }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Dismissed IDs persisted in localStorage
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(NOTIF_DISMISSED_KEY) || '[]')); }
    catch { return new Set(); }
  });

  const persistDismissed = (next) => {
    setDismissed(next);
    localStorage.setItem(NOTIF_DISMISSED_KEY, JSON.stringify([...next]));
  };

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const upcoming = getUpcomingMarronniers(90);

  // ── Alertes quota ────────────────────────────────────────────────────────
  const QUOTA_LABELS = {
    emails:   { label: 'E-mails',        icon: '📧' },
    ai:       { label: 'Générations IA', icon: '🤖' },
    contacts: { label: 'Contacts',       icon: '👥' },
    sms:      { label: 'Crédits SMS',    icon: '📱' },
  };

  const quotaAlerts = [
    { type: 'emails',   used: tenant?.emails_used ?? 0,   limit: tenant?.emails_limit ?? 1000 },
    { type: 'ai',       used: tenant?.ai_used ?? 0,       limit: tenant?.ai_limit ?? 20 },
    { type: 'contacts', used: contacts?.length ?? 0,      limit: tenant?.contacts_limit ?? 500 },
    { type: 'sms',      used: (tenant?.sms_limit ?? 50) - (tenant?.sms_credits ?? 0), limit: tenant?.sms_limit ?? 50 },
  ].map(q => ({ ...q, pct: q.limit > 0 ? Math.round(q.used / q.limit * 100) : 0 }))
   .filter(q => q.pct >= 80)
   .map(q => ({
     type: q.pct >= 100 ? 'urgent' : 'marronnier',
     icon: QUOTA_LABELS[q.type].icon,
     text: q.pct >= 100
       ? `Quota ${QUOTA_LABELS[q.type].label} épuisé (${q.used}/${q.limit})`
       : `Quota ${QUOTA_LABELS[q.type].label} à ${q.pct}% (${q.used}/${q.limit})`,
     sub: q.pct >= 100
       ? 'Les actions sont bloquées — passez au plan supérieur'
       : 'Il vous reste peu — pensez à mettre à jour votre plan',
     link: '/settings?tab=plan',
     cta: 'Voir mon plan →',
   }));

  // Build unified notification list
  const notifs = [
    ...quotaAlerts,
    ...urgentItems.map(u => ({ type: 'urgent', icon: u.icon, text: u.text, link: u.link, cta: u.cta })),
    ...upcoming.slice(0, 3).map(m => ({
      type: 'marronnier',
      icon: m.icon,
      text: `${m.name}${m.diffDays === 0 ? " — c'est aujourd'hui !" : m.diffDays === 1 ? " — c'est demain !" : ` — dans ${m.diffDays} jour${m.diffDays > 1 ? 's' : ''}`}`,
      sub: `Pense à préparer ${m.campaign}`,
      link: '/campaigns/new',
      // Coup d'avance : la campagne arrive DÉJÀ construite (nom, objectif,
      // canal, sujet, ton, cible) + un corps prêt — il n'y a plus qu'à valider.
      state: {
        startStep: 1,
        preset: {
          ...m.preset,
          body: m.preset?.body
            ?? `Bonjour {{prénom}},\n\nEn ce jour un peu spécial, on tenait à vous adresser un petit mot — passez une très belle journée !\nEt si vous passez nous voir prochainement, une petite attention vous attend.\n\nÀ très bientôt !`,
        },
      },
      cta: 'Créer la campagne →',
    })),
    ...strategies.map(s => {
      const { pct, isBehind } = getStratStatus(s);
      return {
        type: isBehind ? 'urgent' : 'info',
        icon: s.icon || '🧠',
        text: `${s.title}`,
        sub: isBehind ? `En retard — ${pct}% complété` : `${pct}% complété`,
        link: '/strategy',
        cta: 'Voir la stratégie →',
      };
    }),
  ];

  // Filter out dismissed
  const visible     = notifs.filter(n => !dismissed.has(notifId(n)));
  const urgentCount = visible.filter(n => n.type === 'urgent').length;
  const total       = visible.length;

  const dismissOne = (n) => {
    persistDismissed(new Set([...dismissed, notifId(n)]));
  };
  const dismissAll = () => {
    persistDismissed(new Set([...dismissed, ...notifs.map(notifId)]));
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="notif-bell-btn" onClick={() => setOpen(o => !o)} title="Notifications">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {total > 0 && (
          <span className={`notif-bell-badge${urgentCount > 0 ? ' notif-bell-badge-urgent' : ''}`}>
            {total}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">
            <span className="notif-dropdown-title">🔔 Notifications</span>
            {total > 0
              ? <button className="notif-markread-btn" onClick={dismissAll}>Tout marquer lu ✓</button>
              : <span className="notif-dropdown-count">0 message</span>
            }
          </div>
          <div className="notif-dropdown-list">
            {total === 0 ? (
              <div className="notif-empty">✅ Rien à signaler — tout est au top !</div>
            ) : visible.map((n, i) => (
              <div key={i} className={`notif-item notif-item-${n.type}`}>
                <span className="notif-item-icon">{n.icon}</span>
                <div className="notif-item-body">
                  <div className="notif-item-text">{n.text}</div>
                  {n.sub && <div className="notif-item-sub">{n.sub}</div>}
                </div>
                <div className="notif-item-right">
                  {n.link && (
                    <Link to={n.link} state={n.state} className="notif-item-cta" onClick={() => setOpen(false)}>{n.cta}</Link>
                  )}
                  <button className="notif-dismiss-btn" onClick={() => dismissOne(n)} title="Marquer comme lu" aria-label="Marquer comme lu">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
          {total > 0 && (
            <div className="notif-dropdown-footer">
              <Link to="/strategy" onClick={() => setOpen(false)} className="notif-footer-link">Voir mes stratégies →</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Open Preview Button — réutilisable partout ───────────────────────────────
function OpenPreviewBtn({ id, className = 'db2-icon-btn', title = 'Aperçu fiche' }) {
  const { openPreview } = useContactPreview();
  return (
    <button onClick={() => openPreview(id)} className={className} title={title} style={{ cursor: 'pointer' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
    </button>
  );
}

// ── IA Status Button — toujours vert ─────────────────────────────────────────
function IaStatusButton({ aiUsed, aiLimit }) {
  const remaining = Math.max(0, aiLimit - aiUsed);
  return (
    <div className="ia-status-wrap">
      <Link to="/settings?tab=plan" className="ia-status-btn ia-status-active"
        title={`IA Appee active · ${remaining} crédits restants`}
        aria-label={`IA Appee active, ${remaining} crédits restants`}>
        <span className="ia-status-dot ia-dot-green" />
        <span className="ia-status-label">IA</span>
      </Link>
    </div>
  );
}

// ── SMS Status Button ─────────────────────────────────────────────────────────
function SmsStatusButton({ credits, creditsLimit }) {
  const isEmpty  = credits === 0;
  const isLow    = !isEmpty && credits <= 20;
  const dotColor = isEmpty ? 'ia-dot-red' : isLow ? 'ia-dot-orange' : 'ia-dot-green';
  const label    = isEmpty ? 'SMS épuisés' : isLow ? `SMS ${credits}` : `SMS ${credits}`;
  const title    = isEmpty
    ? 'Crédits SMS épuisés — rechargez pour continuer les envois'
    : isLow
      ? `Plus que ${credits} crédit${credits > 1 ? 's' : ''} SMS — pensez à recharger`
      : `${credits} crédits SMS restants`;

  if (!isEmpty && !isLow) return null; // masqué si tout va bien

  return (
    <div className="ia-status-wrap">
      <Link to="/settings?tab=plan" className={`ia-status-btn ${isEmpty ? 'ia-status-empty' : 'ia-status-warn'}`} title={title}>
        <span className={`ia-status-dot ${dotColor}`} />
        <span className="ia-status-label">{label}</span>
      </Link>
      <Link to="/settings?tab=plan" className="ia-add-credits-btn">
        {isEmpty ? '🔴 Recharger SMS' : '🟠 Recharger'}
      </Link>
    </div>
  );
}

// ── SMS Compose Button + Popup ────────────────────────────────────────────────
// Groupes prédéfinis basés sur les contacts
function buildGroups(contacts) {
  const withPhone = (contacts || []).filter(c => c.phone);
  const now = new Date();
  const thisMonth = now.getMonth() + 1;
  return [
    { id: 'all',        label: 'Tous les contacts',     icon: '👥', contacts: withPhone },
    { id: 'vip',        label: 'VIP',                   icon: '⭐', contacts: withPhone.filter(c => c.category === 'VIP') },
    { id: 'bday_month', label: 'Anniversaires ce mois', icon: '🎂', contacts: withPhone.filter(c => { if (!c.birth_date) return false; const m = parseInt(c.birth_date.slice(5,7),10); return m === thisMonth; }) },
    { id: 'inactive',   label: 'Clients inactifs',      icon: '💤', contacts: withPhone.filter(c => c.engagement_score !== undefined ? c.engagement_score < 30 : false) },
    { id: 'custom',     label: 'Sélection manuelle',    icon: '✏️', contacts: [] },
  ];
}

function SmsComposeButton({ contacts, tenant, addNotification }) {
  const credits = tenant?.sms_credits ?? 0;
  const [open, setOpen]     = useState(false);
  const [mode, setMode]     = useState('solo');   // 'solo' | 'group'
  const [step, setStep]     = useState('compose'); // 'compose' | 'preview'

  // solo
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);

  // group
  const [groupId, setGroupId]         = useState(null);
  const [customList, setCustomList]   = useState([]);
  const [groupSearch, setGroupSearch] = useState('');

  // message
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const groups = useMemo(() => buildGroups(contacts), [contacts]);

  // Liste effective de destinataires
  const recipients = useMemo(() => {
    if (mode === 'solo') {
      if (selected) return [selected];
      const manualPhone = /^[+\d\s]{6,}$/.test(search.trim()) ? search.trim() : null;
      if (manualPhone) return [{ phone: manualPhone, first_name: manualPhone, last_name: '', id: null }];
      return [];
    }
    if (groupId === 'custom') return customList;
    const g = groups.find(g => g.id === groupId);
    return g ? g.contacts : [];
  }, [mode, selected, search, groupId, customList, groups]);

  // Coût réel : un message > 160 car (ou avec emoji → limite 70) part en plusieurs
  // segments, chacun facturé/débité. needed = destinataires × segments.
  const smsMeta  = smsInfo(message);
  const seg      = smsMeta.segments;
  const needed   = recipients.length * seg;
  const enough   = credits >= needed;
  const canNext  = recipients.length > 0 && message.trim().length > 0;

  const filteredSolo = useMemo(() => {
    if (!search.trim() || selected) return [];
    const q = search.toLowerCase();
    return (contacts || []).filter(c => c.phone && (`${c.first_name} ${c.last_name}`.toLowerCase().includes(q) || (c.phone||'').includes(q))).slice(0, 6);
  }, [search, contacts, selected]);

  const filteredGroup = useMemo(() => {
    if (!groupSearch.trim()) return [];
    const q = groupSearch.toLowerCase();
    return (contacts || []).filter(c => c.phone && !customList.find(x => x.id === c.id) && (`${c.first_name} ${c.last_name}`.toLowerCase().includes(q) || (c.phone||'').includes(q))).slice(0, 6);
  }, [groupSearch, contacts, customList]);

  const [sentCount, setSentCount] = useState(0);

  const closeModal = () => { // eslint-disable-line
    setOpen(false);
    // reset différé pour ne pas flasher pendant l'animation de fermeture
    setTimeout(() => {
      setStep('compose'); setMode('solo'); setSearch(''); setSelected(null);
      setGroupId(null); setCustomList([]); setGroupSearch(''); setMessage('');
      setSentCount(0); setSending(false);
    }, 300);
  };

  const handleSend = async () => {
    if (!canNext || !enough || sending) return;
    setSending(true);
    const count = needed;
    try {
      if (mode === 'solo' && recipients.length === 1) {
        await api.sendSms({ phone: recipients[0].phone, message: message.trim(), contact_id: recipients[0].id || null, type: 'campaign' });
      } else {
        const payload = recipients.map(r => ({ phone: r.phone, contact_id: r.id || null }));
        await api.sendSmsBulk({ recipients: payload, message: message.trim(), type: 'campaign' });
      }
      setSentCount(count);
      setStep('success');
    } catch {
      addNotification?.({ type: 'error', message: 'Erreur lors de l\'envoi.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="ia-status-btn ia-status-active sms-compose-btn" title="Envoyer un SMS" style={{ background: '#5A35FF', color: '#fff', border: 'none', gap: 6 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        <span className="ia-status-label" style={{ color: '#fff' }}>SMS</span>
      </button>

      {open && (
        <div className="sms-compose-panel" onClick={e => e.target === e.currentTarget && closeModal()}>
        <div className="sms-compose-inner">

          {/* ── Header ── */}
          <div className="sms-compose-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {step === 'preview' && (
                <button onClick={() => setStep('compose')} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', color: '#fff', fontSize: 13, display:'flex',alignItems:'center',justifyContent:'center' }}>←</button>
              )}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              <span>{step === 'success' ? 'SMS envoyé !' : step === 'preview' ? 'Aperçu & Confirmation' : 'Nouveau SMS'}</span>
            </div>
            <button onClick={closeModal} className="sms-compose-close">✕</button>
          </div>

          {step === 'compose' ? (<>

            {/* ── Solde SMS toujours visible + rachat 1 clic sous le seuil bas ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 16px', borderBottom: '1px solid var(--line)', background: credits <= SMS_LOW_THRESHOLD ? '#FFF7ED' : '#F9FAFB', fontSize: 12.5 }}>
              <span style={{ color: credits <= SMS_LOW_THRESHOLD ? '#92400E' : '#374151', fontWeight: 700 }}>
                💳 {credits} crédit{credits > 1 ? 's' : ''} SMS restant{credits > 1 ? 's' : ''}
              </span>
              {credits <= SMS_LOW_THRESHOLD && (() => {
                const idx = recommendedPack(Math.max(1, SMS_LOW_THRESHOLD - credits + 30));
                const pack = SMS_PACKS[idx];
                return (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const res = await api.checkoutPack('sms', idx, '/dashboard');
                        if (res?.url) window.location.href = res.url;
                      } catch { addNotification?.('Paiement indisponible pour le moment.', 'error'); }
                    }}
                    style={{ marginLeft: 'auto', border: 'none', background: '#5A35FF', color: '#fff', borderRadius: 999, padding: '6px 13px', fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                  >
                    💳 Pack {pack.credits} SMS — {pack.price} CHF →
                  </button>
                );
              })()}
            </div>

            {/* ── Mode toggle ── */}
            <div className="sms-mode-toggle">
              <button className={`sms-mode-btn ${mode==='solo'?'active':''}`} onClick={() => { setMode('solo'); setGroupId(null); setCustomList([]); }}>
                👤 Un contact
              </button>
              <button className={`sms-mode-btn ${mode==='group'?'active':''}`} onClick={() => setMode('group')}>
                👥 Groupe
              </button>
            </div>

            {/* ── Destinataire(s) ── */}
            <div className="sms-compose-to-row" style={{ alignItems: 'flex-start', minHeight: 'auto', padding: '12px 16px' }}>
              <span className="sms-compose-to-label" style={{ paddingTop: 2 }}>À :</span>
              <div style={{ flex: 1 }}>

                {mode === 'solo' && (
                  selected ? (
                    <div className="sms-compose-selected">
                      <span>👤 {selected.first_name} {selected.last_name} · {selected.phone}</span>
                      <button onClick={() => { setSelected(null); setSearch(''); }} className="sms-compose-deselect">✕</button>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <input className="sms-compose-input-inline" placeholder="Nom ou numéro de téléphone…" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
                      {filteredSolo.length > 0 && (
                        <div className="sms-compose-suggestions">
                          {filteredSolo.map(c => (
                            <button key={c.id} className="sms-compose-suggestion" onClick={() => { setSelected(c); setSearch(''); }}>
                              <span className="sms-sugg-name">{c.first_name} {c.last_name}</span>
                              <span className="sms-sugg-phone">{c.phone}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                )}

                {mode === 'group' && (<>
                  {/* Segments prédéfinis */}
                  <div className="sms-group-chips">
                    {groups.filter(g => g.id !== 'custom').map(g => (
                      <button key={g.id} onClick={() => setGroupId(g.id === groupId ? null : g.id)}
                        className={`sms-group-chip ${groupId === g.id ? 'active' : ''} ${g.contacts.length === 0 ? 'disabled' : ''}`}
                        disabled={g.contacts.length === 0}>
                        {g.icon} {g.label}
                        <span className="sms-chip-count">{g.contacts.length}</span>
                      </button>
                    ))}
                    <button onClick={() => setGroupId(groupId === 'custom' ? null : 'custom')}
                      className={`sms-group-chip ${groupId === 'custom' ? 'active' : ''}`}>
                      ✏️ Sélection manuelle
                      {customList.length > 0 && <span className="sms-chip-count">{customList.length}</span>}
                    </button>
                  </div>
                  {/* Sélection manuelle */}
                  {groupId === 'custom' && (<>
                    <div style={{ position: 'relative', marginTop: 8 }}>
                      <input className="sms-compose-input-inline" style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 10px', width: '100%', boxSizing: 'border-box' }}
                        placeholder="Ajouter un contact…" value={groupSearch} onChange={e => setGroupSearch(e.target.value)} />
                      {filteredGroup.length > 0 && (
                        <div className="sms-compose-suggestions">
                          {filteredGroup.map(c => (
                            <button key={c.id} className="sms-compose-suggestion" onClick={() => { setCustomList(l => [...l, c]); setGroupSearch(''); }}>
                              <span className="sms-sugg-name">{c.first_name} {c.last_name}</span>
                              <span className="sms-sugg-phone">{c.phone}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {customList.length > 0 && (
                      <div className="sms-custom-tags">
                        {customList.map(c => (
                          <span key={c.id} className="sms-custom-tag">
                            {c.first_name} {c.last_name}
                            <button onClick={() => setCustomList(l => l.filter(x => x.id !== c.id))}>✕</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </>)}
                  {/* Résumé groupe */}
                  {recipients.length > 0 && (
                    <div className="sms-group-summary">
                      ✅ {recipients.length} destinataire{recipients.length > 1 ? 's' : ''} sélectionné{recipients.length > 1 ? 's' : ''}
                    </div>
                  )}
                </>)}

              </div>
            </div>

            {/* ── Message ── */}
            <div style={{ borderTop: '1px solid #F3F4F6' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 16px 2px' }}>
                <span className={`sms-compose-counter ${seg > 1 ? 'danger' : smsMeta.chars > 140 ? 'warn' : ''}`}>
                  {smsMeta.chars} car. · {seg} SMS{smsMeta.unicode ? ' · emoji → 70/SMS' : ''}
                </span>
              </div>
              <textarea className="sms-compose-textarea" placeholder="Écris ton message SMS…" value={message} onChange={e => setMessage(e.target.value.slice(0, 160))} maxLength={160} />
            </div>

            {/* ── Footer compose ── */}
            <div className="sms-compose-footer">
              <button onClick={closeModal} className="sms-compose-cancel">Annuler</button>
              <button onClick={() => setStep('preview')} disabled={!canNext} className="sms-compose-send">
                Aperçu →
              </button>
            </div>

          </>) : (<>

            {/* ══ ÉTAPE PREVIEW ══ */}
            {/* Bulle preview */}
            <div style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Aperçu du message</div>
              <div className="sms-preview-bubble">
                <div className="sms-preview-text">{message}</div>
                <div className="sms-preview-meta">
                  {smsMeta.chars} car. · {seg} SMS{recipients.length > 1 ? ` × ${recipients.length} destinataires` : ''}
                  {smsMeta.unicode && <span style={{ color: '#DC2626', marginLeft: 6 }}>· emoji = Unicode (70 car/SMS)</span>}
                </div>
              </div>
            </div>

            {/* Destinataires */}
            <div style={{ padding: '0 20px 12px', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Destinataires</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 80, overflowY: 'auto' }}>
                {recipients.slice(0, 12).map((r, i) => (
                  <span key={i} style={{ fontSize: 12, background: '#F5F3FF', color: '#5A35FF', borderRadius: 999, padding: '3px 10px', fontWeight: 600 }}>
                    {r.first_name} {r.last_name || ''}
                  </span>
                ))}
                {recipients.length > 12 && <span style={{ fontSize: 12, color: '#9CA3AF' }}>+{recipients.length - 12} autres</span>}
              </div>
            </div>

            {/* Bilan crédits */}
            <div style={{ padding: '14px 20px' }}>
              <div className={`sms-credit-summary ${enough ? 'ok' : 'nok'}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 700 }}>💳 Crédits nécessaires</span>
                  <span style={{ fontWeight: 900, fontSize: 18 }}>{needed}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                  <span>Crédits disponibles</span>
                  <span style={{ fontWeight: 700, color: enough ? '#065F46' : '#991B1B' }}>{credits}</span>
                </div>
                {/* barre */}
                <div style={{ height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
                  <div style={{ height: '100%', borderRadius: 3, width: `${Math.min(100, Math.round((Math.min(needed,credits)/Math.max(needed,1))*100))}%`, background: enough ? '#10B981' : '#EF4444', transition: 'width .4s' }} />
                </div>
                {enough ? (
                  <div style={{ fontSize: 12, color: '#065F46' }}>✅ Vous avez assez de crédits. Il en restera <strong>{credits - needed}</strong> après envoi.</div>
                ) : (
                  <div style={{ fontSize: 12, color: '#991B1B', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>⚠️ Il manque <strong>{needed - credits}</strong> crédit{needed - credits > 1 ? 's' : ''}.</span>
                    {(() => {
                      const idx = recommendedPack(needed - credits);
                      const pack = SMS_PACKS[idx];
                      return (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const res = await api.checkoutPack('sms', idx, '/dashboard');
                              if (res?.url) window.location.href = res.url;
                            } catch { addNotification?.('Paiement indisponible pour le moment.', 'error'); }
                          }}
                          style={{ border: 'none', background: '#5A35FF', color: '#fff', borderRadius: 999, padding: '5px 12px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          💳 Pack {pack.credits} SMS — {pack.price} CHF →
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Footer preview */}
            <div className="sms-compose-footer">
              <button onClick={() => setStep('compose')} className="sms-compose-cancel">← Modifier</button>
              <button onClick={handleSend} disabled={!enough || sending} className="sms-compose-send" style={{ minWidth: 140 }}>
                {sending ? 'Envoi en cours…' : `Envoyer ${recipients.length} SMS →`}
              </button>
            </div>

          </>)}

          {/* ══ ÉTAPE SUCCESS ══ */}
          {step === 'success' && (
            <div style={{ padding: '36px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#10B981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, boxShadow: '0 8px 24px rgba(16,185,129,.3)', animation: 'compose-slide-up .3s ease' }}>
                ✓
              </div>
              <div style={{ fontWeight: 800, fontSize: 18, color: '#1F2937' }}>
                {sentCount > 1 ? `${sentCount} SMS envoyés !` : 'SMS envoyé !'}
              </div>
              <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
                {sentCount > 1
                  ? `Ton message est en route vers ${sentCount} destinataires.`
                  : 'Ton message est en route.'}
                <br />Les envois apparaîtront dans <strong>Analytics → SMS</strong>.
              </div>
              <button onClick={closeModal} className="sms-compose-send" style={{ marginTop: 8, padding: '10px 28px' }}>
                Fermer
              </button>
            </div>
          )}

        </div>
        </div>
      )}
    </>
  );
}

// ── Appee Feed Ticker ─────────────────────────────────────────────────────────
function AppeeFeedTicker({ todayCount, weekCount, tipsCount, autoSentCount, missingEmail, recommendations, todayBdays, tenant }) {
  // Build dynamic feed items from real data
  const items = useMemo(() => {
    const list = [];

    // Briefing summary — always first
    list.push({
      id: 'briefing',
      icon: '✨',
      label: `J'ai scanné ta journée relationnelle : ${todayCount} anniversaire${todayCount !== 1 ? 's' : ''} aujourd'hui, ${weekCount} à venir, ${tipsCount} conseil${tipsCount !== 1 ? 's' : ''} utile${tipsCount !== 1 ? 's' : ''}.`,
      actionLabel: 'Voir le briefing',
      href: '/birthdays',
    });

    // Today's round-age or VIP birthdays
    const priority = todayBdays.find(c => {
      const age = contactAge(c.birth_date);
      return c.category === 'VIP' || (age && age % 10 === 0);
    });
    if (priority) {
      const age = contactAge(priority.birth_date);
      list.push({
        id: 'bday-priority',
        icon: '🎂',
        label: age && age % 10 === 0
          ? `${priority.first_name} fête ses ${age} ans aujourd'hui. Un appel peut vraiment marquer le coup.`
          : `${priority.first_name} ${priority.last_name} est un contact VIP — aujourd'hui c'est son anniversaire.`,
        actionLabel: 'Voir sa fiche',
        href: `/contacts/${priority.id}`,
      });
    }

    // Messages sent status
    if (autoSentCount > 0) {
      list.push({
        id: 'sent',
        icon: '✅',
        label: `${autoSentCount} message${autoSentCount > 1 ? 's ont' : ' a'} déjà été envoyé${autoSentCount > 1 ? 's' : ''} aujourd'hui. Tu n'as rien à faire.`,
        actionLabel: 'Voir le suivi',
        href: '/birthdays?tab=today',
      });
    } else if (todayCount > 0) {
      list.push({
        id: 'sending',
        icon: '✅',
        label: 'Les messages du jour sont prêts ou déjà envoyés. Tu n\'as rien à faire pour les envois.',
        actionLabel: 'Voir le suivi',
        href: '/birthdays?tab=today',
      });
    }

    // Missing email warning
    if (missingEmail > 0) {
      list.push({
        id: 'data-quality',
        icon: '🔧',
        label: `${missingEmail} fiche${missingEmail > 1 ? 's prioritaires peuvent' : ' prioritaire peut'} être complétée${missingEmail > 1 ? 's' : ''} pour des messages plus personnalisés.`,
        actionLabel: 'Compléter maintenant',
        href: '/contacts?noEmail=1',
      });
    }

    // AI credits warning
    const aiRemaining = Math.max(0, (tenant?.ai_limit ?? 50) - (tenant?.ai_used ?? 0));
    if (aiRemaining === 0) {
      list.push({
        id: 'ai-credits',
        icon: '🤖',
        label: 'L\'IA Appee est en pause : ajoute des crédits pour continuer à générer des conseils personnalisés.',
        actionLabel: 'Ajouter crédits IA',
        href: '/settings?tab=plan',
      });
    }

    // Week birthdays
    if (weekCount > 0) {
      list.push({
        id: 'week',
        icon: '📅',
        label: `${weekCount} anniversaire${weekCount > 1 ? 's' : ''} cette semaine — je surveille les prochains moments importants.`,
        actionLabel: 'Voir la semaine',
        href: '/birthdays?tab=week',
      });
    }

    // ── Anniversaires ratés hier (email failed ou non envoyé) ──────────────────
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    const ym = yesterday.getMonth() + 1;
    const yd = yesterday.getDate();
    const missedYesterday = (todayBdays.__all || []).filter ? [] : (() => {
      // On utilise weekBdays pour chercher les contacts dont l'anniversaire était hier
      return (recommendations || []).filter(r => {
        if (!r.birth_date) return false;
        const [,m,d] = r.birth_date.split('-');
        return parseInt(m) === ym && parseInt(d) === yd && !r.last_email_sent?.startsWith(yesterday.toISOString().slice(0,10));
      });
    })();
    if (missedYesterday.length > 0) {
      list.unshift({
        id: 'missed-bday',
        icon: '⚠️',
        label: `${missedYesterday.length} anniversaire${missedYesterday.length > 1 ? 's' : ''} n'a pas été couvert${missedYesterday.length > 1 ? 's' : ''} hier. Je te propose de leur envoyer un message aujourd'hui.`,
        actionLabel: 'Voir les fiches →',
        href: '/birthdays',
      });
    }

    // Default fallback if nothing
    if (list.length === 0) {
      list.push({
        id: 'all-ok',
        icon: '💜',
        label: 'Tout est sous contrôle. Appee veille sur les prochains moments importants.',
        actionLabel: 'Voir le dashboard',
        href: '/birthdays',
      });
    }

    return list;
  }, [todayCount, weekCount, tipsCount, autoSentCount, missingEmail, recommendations, todayBdays, tenant]);

  // Icons for each item type
  const ITEM_ICONS = {
    briefing:      ( <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#6E45F5" strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="3"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> ),
    'bday-priority':( <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#F59E0B" strokeWidth="2.2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> ),
    sent:          ( <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#16B886" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg> ),
    sending:       ( <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#16B886" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg> ),
    'data-quality':( <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#FF9800" strokeWidth="2.2" strokeLinecap="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg> ),
    week:          ( <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#6E45F5" strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="3"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> ),
    'ai-credits':  ( <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#EF4444" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> ),
    'all-ok':      ( <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#16B886" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg> ),
    'missed-bday': ( <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#EF4444" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> ),
  };

  const [activeIdx, setActiveIdx] = useState(0);
  // Auto-advance every 5s
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setActiveIdx(i => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length]);

  const cur = items[activeIdx];

  return (
    <div className="fil-appee-wrap">
      {/* Left pill — fixed gradient */}
      <div className="fil-appee-label">
        <span className="fil-appee-title">FIL APPEE ✨</span>
        <span className="fil-appee-sub">Ton fil d'actualités<br/>relationnelles</span>
      </div>

      {/* Right — single item carousel */}
      <div className="fil-appee-carousel">
        <Link key={activeIdx} to={cur.href} className="fil-appee-slide">
          <div className="fil-appee-slide-icon">
            {ITEM_ICONS[cur.id] || ITEM_ICONS['briefing']}
          </div>
          <div className="fil-appee-slide-body">
            <span className="fil-appee-slide-text">{cur.label}</span>
            <span className="fil-appee-slide-cta">{cur.actionLabel} →</span>
          </div>
        </Link>

        {/* Progress dots inside carousel */}
        {items.length > 1 && (
          <div className="fil-appee-dots">
            {items.map((_, i) => (
              <button key={i} className={`fil-appee-dot ${i === activeIdx ? 'fil-appee-dot-active' : ''}`}
                onClick={e => { e.preventDefault(); setActiveIdx(i); }} aria-label={`Item ${i+1}`} />
            ))}
          </div>
        )}
      </div>

      {/* Arrow nav */}
      {items.length > 1 && (
        <button className="fil-appee-arrow" onClick={() => setActiveIdx(i => (i + 1) % items.length)} aria-label="Suivant">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      )}
    </div>
  );
}

// ─── Marketing status helpers ────────────────────────────────────────────────
function calcMarketingStatus(cp) {
  if (!cp) return { score: 0, goods: [], improvements: [] };
  const goods = [], improvements = [];

  const S = '/settings?tab=entreprise'; // lien direct vers profil entreprise

  // Profil
  if (cp.name)                  goods.push({ icon: '🏢', text: 'Nom de l\'entreprise renseigné' });
  else                          improvements.push({ icon: '🏢', text: 'Ajouter le nom de l\'entreprise', prio: 3, link: S });
  if (cp.target_audience)       goods.push({ icon: '🎯', text: 'Clientèle cible définie' });
  else                          improvements.push({ icon: '🎯', text: 'Définir votre clientèle cible', prio: 3, link: S });
  if (cp.services)              goods.push({ icon: '⚡', text: 'Services & produits documentés' });
  else                          improvements.push({ icon: '⚡', text: 'Décrire vos services/produits', prio: 2, link: S });
  if (cp.unique_selling_point)  goods.push({ icon: '💎', text: 'Argument unique défini' });
  else                          improvements.push({ icon: '💎', text: 'Définir votre argument unique', prio: 2, link: S });
  if (cp.communication_tone)    goods.push({ icon: '🗣️', text: 'Ton de communication choisi' });
  else                          improvements.push({ icon: '🗣️', text: 'Choisir votre ton de communication', prio: 1, link: S });
  if (cp.logo_url)              goods.push({ icon: '🖼️', text: 'Logo uploadé' });
  else                          improvements.push({ icon: '🖼️', text: 'Uploader votre logo', prio: 1, link: S });

  // Réseaux sociaux
  const nets = [cp.facebook_url, cp.instagram_url, cp.linkedin_url].filter(Boolean).length;
  if (nets >= 3)      goods.push({ icon: '📱', text: '3 réseaux sociaux connectés' });
  else if (nets >= 1) goods.push({ icon: '📱', text: `${nets} réseau${nets > 1 ? 'x' : ''} social connecté` });
  if (nets < 3)       improvements.push({ icon: '📱', text: `Connecter ${nets === 0 ? 'vos réseaux sociaux' : 'les réseaux manquants'}`, prio: nets === 0 ? 3 : 1, link: S });

  if (cp.gmb_has_page === true)  goods.push({ icon: '📍', text: 'Fiche Google My Business active' });
  if (cp.gmb_has_page === false) improvements.push({ icon: '📍', text: 'Créer une fiche Google My Business', prio: 3, link: S });

  // Audit
  const auditScore = cp.marketing_audit?.overall_score;
  if (auditScore != null) {
    if (auditScore >= 70) goods.push({ icon: '📊', text: `Audit marketing : ${auditScore}/100 ✨` });
    else                  improvements.push({ icon: '📊', text: `Améliorer le score d'audit (${auditScore}/100)`, prio: 2, link: S });
  } else {
    improvements.push({ icon: '📊', text: 'Lancer votre premier audit marketing', prio: 2, link: S });
  }

  // Score
  let s = 0;
  if (cp.name) s += 10; if (cp.target_audience) s += 12; if (cp.services) s += 12;
  if (cp.unique_selling_point) s += 10; if (cp.communication_tone) s += 8; if (cp.price_range) s += 5;
  if (cp.logo_url) s += 8; if (cp.facebook_url) s += 5; if (cp.instagram_url) s += 5;
  if (cp.linkedin_url) s += 4; if (cp.google_mybusiness_url || cp.gmb_has_page) s += 8;
  if (auditScore != null) s += 13;

  return { score: Math.min(100, s), goods, improvements: improvements.sort((a, b) => b.prio - a.prio) };
}

const MARKETING_MOTIVATIONS = [
  (score, name) => score >= 80 ? `Ton profil marketing est au top${name} ! 🌟 L'IA a tout ce qu'il faut pour bosser pour toi.` : score >= 60 ? `Beau boulot${name} ! 🚀 Quelques ajouts et tu seras dans le top.` : score >= 40 ? `Tu avances bien${name} ✨ — chaque info que tu ajoutes rend l'IA plus efficace.` : `Let's go${name} ! 🎯 Quelques minutes pour compléter ton profil, et Appee fait le reste.`,
  (score, name) => score >= 80 ? `Profil complet${name} ! 💪 Appee peut générer des campagnes ultra-personnalisées pour toi.` : score >= 60 ? `Presque là${name} ! ✨ Encore un peu et ton profil sera un moteur marketing puissant.` : score >= 40 ? `Chaque étape compte${name} 💡 — ton profil prend forme et l'IA s'améliore avec toi.` : `Bienvenue dans l'aventure${name} ! 🌱 Complète ton profil et regarde l'IA travailler pour toi.`,
  (score, name) => score >= 80 ? `Tu es au max${name} ! 🏆 Continue à alimenter ton profil pour des résultats encore meilleurs.` : score >= 60 ? `Très bonne base${name} 👏 — encore quelques infos et tu débloqueras tout le potentiel d'Appee.` : score >= 40 ? `Bon début${name} 💪 — plus ton profil est complet, plus tes campagnes seront précises.` : `C'est le moment de te lancer${name} ! 🚀 Remplis ton profil et laisse l'IA faire la magie.`,
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, tenant, dashboardData, actions, dismissAction, launchAction, smtp, contacts, templates, addNotification, companyProfile, strategyInstances: strategies, aiEnabled } = useApp();

  // ── Primary data source: /api/dashboard ─────────────────────────────────────
  const stats          = dashboardData?.stats            ?? {};
  const usage          = dashboardData?.usage            ?? {};
  const weekBdays      = dashboardData?.week_birthdays   ?? [];

  // today_birthdays from server, with local fallback derived from weekBdays
  const _todayFromServer = dashboardData?.today_birthdays ?? [];
  const todayBdays = useMemo(() => {
    if (_todayFromServer.length > 0) return _todayFromServer;
    const t = new Date();
    const tm = t.getMonth() + 1;
    const td = t.getDate();
    return weekBdays.filter(c => {
      if (!c.birth_date) return false;
      const [, m, d] = c.birth_date.split('-');
      return parseInt(m) === tm && parseInt(d) === td;
    });
  }, [_todayFromServer, weekBdays]);
  const crmHealth      = dashboardData?.crm_health       ?? null;
  const genderBreakdown= dashboardData?.gender_breakdown ?? null;
  const emailStats30d      = dashboardData?.email_stats_30d    ?? {};
  const birthdaySmsEnabled = dashboardData?.birthday_sms_enabled ?? false;

  // ── Local computations (need full contact array for descriptions/segments) ──
  const missingEmail  = contacts.filter(c => !c.email && c.status === 'active').length;
  const activeActions = actions.filter(a => a.status === 'pending');

  const opportunities = useMemo(() => computeOpportunities(
    contacts,
    stats.dormant_contacts ?? 0,
    stats.vip_contacts     ?? 0,
  ), [contacts, stats.dormant_contacts, stats.vip_contacts]);

  const segments = useMemo(() => ({
    champions: contacts.filter(c => c.quality_score > 80 && c.engagement_score > 60).length,
    loyaux:    contacts.filter(c => c.quality_score > 70 && c.engagement_score >= 40 && c.engagement_score <= 60).length,
    potentiels:contacts.filter(c => c.quality_score >= 50 && c.quality_score <= 70 && c.status === 'active').length,
    perdus:    contacts.filter(c => c.status === 'inactive').length,
  }), [contacts]);

  const [showStrategicDash, setShowStrategicDash] = useState(false);
  const [showMarketingOverview, setShowMarketingOverview] = useState(false);
  const [showAllRecos, setShowAllRecos] = useState(false);   // MES CONSEILS : 3 par défaut
  const [profilOpen, setProfilOpen] = useState(false);        // bloc profil replié si complet

  const noConsentCount = contacts.filter(c => c.gdpr_consent === false).length;
  const noGenderCount  = genderBreakdown
    ? genderBreakdown.non_renseigne
    : contacts.filter(c => !c.gender || c.gender === 'non_renseigne').length;

  const firstName = user?.name?.split(' ')[0] ?? 'toi';
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (aiLoading || aiInsight) return;

    // Quand crédits épuisés : fallback immédiat, pas d'appel API
    if (tenant && !aiEnabled) {
      setAiInsight(getFallbackDashboardInsight(companyProfile, contacts));
      return;
    }

    const today       = new Date().toISOString().slice(0, 10);
    const cacheKey    = `appee_dash_ai_v1_${tenant?.id}`;
    const cacheVerKey = `appee_dash_ai_ver_${tenant?.id}`;
    // Inclure le nom/secteur dans la version → invalide le cache si le profil change
    const profileSig  = `${companyProfile?.name || ''}|${companyProfile?.sector || ''}`;
    const version     = `${contacts.length}|${activeActions.length}|${today}|${profileSig}`;

    try {
      const cachedVer = localStorage.getItem(cacheVerKey);
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedVer === version && cachedRaw) {
        const parsed = JSON.parse(cachedRaw);
        if (parsed && parsed.greeting) { setAiInsight(parsed); return; }
      }
    } catch (_) {}

    setAiLoading(true);
    api.dashboardAiInsight()
      .then(data => {
        if (data && data.greeting) {
          setAiInsight(data);
          try {
            localStorage.setItem(cacheKey,    JSON.stringify(data));
            localStorage.setItem(cacheVerKey, version);
          } catch (_) {}
        }
      })
      .catch(() => {})
      .finally(() => setAiLoading(false));
  }, [tenant?.id, aiEnabled, contacts.length, activeActions.length, companyProfile?.name, companyProfile?.sector]); // eslint-disable-line

  const appeeGreeting = aiInsight?.greeting ?? buildAppeeGreeting(
    stats.today_birthdays ?? todayBdays.length,
    stats.dormant_contacts ?? 0,
    emailStats30d.open_rate ?? 0,
    stats.vip_contacts ?? 0,
    companyProfile,
    contacts.length,
  );

  const urgentItems = [];
  if (!smtp?.verified)
    urgentItems.push({ icon: '⚠️', text: 'Je ne peux pas envoyer tes e-mails — le SMTP n\'est pas encore configuré. Deux minutes et c\'est réglé.', link: '/settings', cta: 'Configurer →' });
  if (missingEmail > 0)
    urgentItems.push({ icon: '📧', text: `${missingEmail} client${missingEmail > 1 ? 's actifs sont' : ' actif est'} sans e-mail — je ne peux pas leur écrire. Dis-moi de les compléter.`, link: '/contacts?noEmail=1', cta: 'Voir les fiches →' });
  if (noConsentCount > 0)
    urgentItems.push({ icon: '🔒', text: `${noConsentCount} contact${noConsentCount > 1 ? 's ont' : ' a'} refusé le consentement RGPD — je les exclus automatiquement de toutes les campagnes.`, link: '/contacts', cta: 'Voir les contacts →' });

  const today    = new Date();
  const dayStr   = format(today, 'EEEE d MMMM yyyy', { locale: fr });
  const dayStrCap = dayStr.charAt(0).toUpperCase() + dayStr.slice(1);

  const monthName = (() => { const n = new Date(); const s = n.toLocaleDateString('fr-FR', { month: 'long' }); return s.charAt(0).toUpperCase() + s.slice(1); })();
  const bdayData = {
    monthName,
    today: todayBdays.length,
    week:  weekBdays.length,
    month: dashboardData?.month_birthdays_count ?? dashboardData?.stats?.month_birthdays ?? 0,
    sent: (() => {
      const now = new Date();
      const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      return contacts.filter(c => c.last_email_sent && c.last_email_sent.slice(0, 7) === ym).length;
    })(),
  };

  // ── Hero work-stats ─────────────────────────────────────────────────────────
  const autoSentTodayCount = todayBdays.filter(c =>
    c.last_email_sent?.slice(0, 10) === todayStr || c.last_email_opened?.slice(0, 10) === todayStr
  ).length;
  const scheduledWeekCount = weekBdays.filter(c => {
    if (!c.birth_date) return false;
    const [, m, d] = c.birth_date.split('-');
    return !(parseInt(m) === today.getMonth() + 1 && parseInt(d) === today.getDate()) && !!c.email;
  }).length;
  const callsAdvisedCount = useMemo(() => {
    const allBdays = [...todayBdays, ...weekBdays.filter(c => !todayBdays.find(t => t.id === c.id))];
    return allBdays.filter(c => {
      const age = getAge(c.birth_date);
      return c.category === 'VIP' || (age && age % 10 === 0);
    }).length;
  }, [todayBdays, weekBdays]);

  // ── Recommendations (contacts who deserve a human call) ───────────────────
  const recommendations = useMemo(() => {
    const recos = [];
    const todayIds = new Set(todayBdays.map(c => c.id));

    // 🧠 « Coup d'avance » : l'info non-évidente que le patron n'aurait pas vue,
    // calculée depuis les signaux du contact (dormance, cap, réceptivité, ancienneté).
    const daysBetween = (d) => d ? Math.round(Math.abs(Date.now() - new Date(d).getTime()) / 86400000) : null;
    const buildInsight = (c, age) => {
      const isRound   = age && age % 10 === 0;
      const lastDays  = daysBetween(c.last_interaction);
      const yrs       = c.created_at ? Math.floor((daysBetween(c.created_at) || 0) / 365) : null;
      const engaged   = (c.emails_clicked > 0) || (c.emails_opened >= 3);
      if (isRound)
        return `Cap des ${age} ans 🎯 — un appel personnel marquera 10× plus qu'un simple message.`;
      if (lastDays != null && lastDays > 120)
        return `Pas passé depuis ${Math.round(lastDays / 30)} mois 💤 — son anniversaire est LE prétexte parfait pour le faire revenir.`;
      if (engaged)
        return `Il ouvre tes messages 👀 — il est réceptif, c'est le moment idéal pour un mot perso.`;
      if (yrs && yrs >= 3)
        return `Client fidèle depuis ${yrs} ans 💜 — un client qu'on n'oublie jamais est un client qui reste.`;
      if (c.category === 'VIP')
        return `Client VIP ⭐ — soigne-le particulièrement aujourd'hui, ça se retient.`;
      // Repli : toujours un coup d'avance, même sans signal fort.
      return `Tu es sûrement le seul commerçant à penser à son anniversaire aujourd'hui — c'est exactement ce qui te rend inoubliable.`;
    };

    // Priorité DANS le jour : âge rond d'abord (le plus important), puis VIP
    const bdayWeight = (c) => {
      const a = getAge(c.birth_date);
      return (a && a % 10 === 0 ? 2 : 0) + (/vip/i.test(c.category || '') ? 1 : 0);
    };
    [...todayBdays].sort((a, b) => bdayWeight(b) - bdayWeight(a)).forEach(c => {
      const cAge = getAge(c.birth_date);
      // Anniversaire du JOUR : toujours en priorité 1, même sans conseil pré-généré
      const conseil = getConseilMessage(c)
        || `C'est son anniversaire aujourd'hui${cAge ? ` (${cAge} ans)` : ''} — un mot ou un appel personnel fera toute la différence.`;
      const actionType = c.phone ? 'Appel' : c.email ? 'E-mail' : null;
      recos.push({
        priority: 1,
        icon: '📞',
        title: `Appelle ${c.first_name} ${c.last_name}`,
        name: `${c.first_name} ${c.last_name}`.trim(),
        age: cAge,
        city: c.city || null,
        insight: buildInsight(c, cAge),
        desc: c.ai_conseil || conseil,
        memory: c.relationship_memory || null,
        when: 'Aujourd\'hui',
        whenDiff: 0,
        actionType,
        phone: c.phone,
        link: `/contacts/${c.id}`,
      });
    });

    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    weekBdays.filter(c => !todayIds.has(c.id)).forEach(c => {
      const age = getAge(c.birth_date);
      const isRound = age && age % 10 === 0;
      const isVip = c.category === 'VIP';
      if (!isVip && !isRound) return;
      const [, m, d] = (c.birth_date || '').split('-');
      const bday = new Date(today.getFullYear(), parseInt(m) - 1, parseInt(d));
      const diff = Math.max(0, Math.round((bday - todayMidnight) / 86400000));
      const when = diff === 0 ? 'Aujourd\'hui' : diff === 1 ? 'Demain' : `Dans ${diff}j`;
      const actionType = c.phone ? 'Appel' : c.email ? 'E-mail' : null;
      recos.push({
        priority: 2,
        icon: isVip && isRound ? '🌟' : isVip ? '⭐' : '🎯',
        title: `Prépare un appel pour ${c.first_name} ${c.last_name}`,
        name: `${c.first_name} ${c.last_name}`.trim(),
        age,
        city: c.city || null,
        insight: buildInsight(c, age),
        desc: c.ai_conseil || (isRound && isVip
          ? `Fête ses ${age} ans ${diff <= 1 ? (diff === 0 ? 'aujourd\'hui' : 'demain') : `dans ${diff}j`} — anniversaire rond + contact important. Le message part automatiquement, mais un appel fera beaucoup plus.`
          : isRound
          ? `Fête ses ${age} ans ${diff <= 1 ? (diff === 0 ? 'aujourd\'hui' : 'demain') : `dans ${diff}j`} — un cap symbolique. Un appel personnel marquerait vraiment les esprits.`
          : `Contact important, anniversaire ${diff <= 1 ? (diff === 0 ? 'aujourd\'hui' : 'demain') : `dans ${diff}j`} — ta présence humaine fera la différence.`),
        when,
        whenDiff: diff,
        actionType,
        memory: c.relationship_memory || null,
        phone: c.phone,
        link: `/contacts/${c.id}`,
      });
    });

    // ── Marroniers — seulement si le tenant a des contacts à cibler ──────
    if (contacts.length > 0) {
      getUpcomingMarronniers(60).slice(0, 3).forEach(m => {
        recos.push({
          priority: 3,
          icon: m.icon,
          title: `Prépare une campagne — ${m.name}`,
          desc: `${m.name} dans ${m.diffDays} jour${m.diffDays > 1 ? 's' : ''} — crée une campagne personnalisée pour marquer l'occasion avec tes clients.`,
          when: m.diffDays === 0 ? "Aujourd'hui" : m.diffDays === 1 ? 'Demain' : `Dans ${m.diffDays}j`,
          whenDiff: m.diffDays,
          actionType: 'Campagne',
          link: '/campaigns/new',
          preset: m.preset ?? null,   // ← filtres + sujet préconfigurés
        });
      });
    }

    // ── Conseils de setup — toujours afficher au moins un conseil ─────────
    // Priorité décroissante : infos entreprise → SMTP → contacts → template → campagne
    if (recos.length === 0) {
      const profileScore = Object.values(companyProfile || {}).filter(v => v && String(v).trim() !== '').length;
      if (profileScore < 5) {
        recos.push({
          priority: 4, icon: '🏢',
          title: 'Complète les infos de ton entreprise',
          desc: 'Plus ton profil est complet, plus les recommandations IA seront précises et personnalisées pour tes clients. C\'est la base de tout.',
          when: 'Maintenant', whenDiff: 0,
          actionType: null,
          link: '/settings',
        });
      } else if (!smtp?.verified) {
        recos.push({
          priority: 4, icon: '📧',
          title: 'Configure ton serveur d\'envoi SMTP',
          desc: 'Sans SMTP configuré, aucun e-mail d\'anniversaire ne partira automatiquement. Configure-le en 2 minutes dans les paramètres.',
          when: 'Maintenant', whenDiff: 0,
          actionType: null,
          link: '/settings',
        });
      } else if (contacts.length < 5) {
        recos.push({
          priority: 4, icon: '👥',
          title: 'Importe tes premiers contacts',
          desc: 'Ajoute tes clients avec leur date d\'anniversaire — Appee s\'occupera du reste automatiquement. Importe un fichier CSV ou ajoute-les un par un.',
          when: 'Maintenant', whenDiff: 0,
          actionType: null,
          link: '/imports',
        });
      } else if (!templates?.some(t => t.type === 'birthday')) {
        recos.push({
          priority: 4, icon: '🎨',
          title: 'Crée ton premier modèle d\'anniversaire',
          desc: 'Tu as des contacts mais pas encore de message d\'anniversaire. Crée un template personnalisé — l\'IA peut le générer en quelques secondes.',
          when: 'Maintenant', whenDiff: 0,
          actionType: null,
          link: '/templates',
        });
      } else {
        recos.push({
          priority: 4, icon: '📣',
          title: 'Lance une campagne pour fidéliser tes clients',
          desc: 'Tout est en place ! Profite-en pour envoyer une campagne à tes clients — une offre, un événement ou simplement un message de fidélité.',
          when: 'Maintenant', whenDiff: 0,
          actionType: 'Campagne',
          link: '/campaigns/new',
        });
      }
    }

    return recos.slice(0, 6);
  }, [todayBdays, weekBdays, companyProfile, smtp, contacts, templates]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // ── Skeleton si dashboardData pas encore chargé ─────────────────────────────
  if (!dashboardData && contacts.length === 0) {
    return (
      <div className="dashboard db2-dashboard">
        <div className="db2-page-header db2-animate-in">
          <div className="db2-page-header-left">
            <div className="db2-skeleton" style={{ width: 220, height: 28, marginBottom: 8 }} />
            <div className="db2-skeleton" style={{ width: 140, height: 16 }} />
          </div>
        </div>
        <div className="db2-skeleton-banner">
          <div style={{ fontSize: 28, marginBottom: 12 }}>🧞</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#5A35FF', marginBottom: 6 }}>Appee se réveille…</div>
          <div style={{ fontSize: 13, color: '#9CA3AF' }}>Je charge tes données et prépare ton briefing du jour.</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
            {[200, 140, 180, 160].map((w, i) => (
              <div key={i} className="db2-skeleton" style={{ width: w, height: 80, borderRadius: 14 }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard db2-dashboard">

      {/* ══════════════════════════════════════════════════════
          PAGE HEADER — outside violet hero
      ══════════════════════════════════════════════════════ */}
      <div className="db2-page-header db2-animate-in" style={{ animationDelay: '0ms' }}>
        <div className="db2-page-header-left">
          <h1 className="db2-page-title">Salut {firstName} ! 👋</h1>
          <p className="db2-page-date">{(() => { const s = format(today, "EEEE d MMMM", { locale: fr }); return s.charAt(0).toUpperCase() + s.slice(1); })()}</p>
        </div>
        <div className="db2-page-header-right">
          {/* 👥 Vue claire contacts / quota — remplace la barre de stats du hero */}
          <Link to="/contacts" className="ia-status-btn ia-status-active" title={`${contacts.length} contacts sur ${(tenant?.contacts_limit ?? 500).toLocaleString('fr-CH')} inclus dans ton plan`}>
            <span style={{ fontSize: 13 }}>👥</span>
            <span className="ia-status-label">{contacts.length.toLocaleString('fr-CH')} <span style={{ opacity: .55 }}>/ {(tenant?.contacts_limit ?? 500).toLocaleString('fr-CH')}</span></span>
          </Link>
          <IaStatusButton aiUsed={tenant?.ai_used ?? 0} aiLimit={tenant?.ai_limit ?? 50} />
          <SmsStatusButton credits={tenant?.sms_credits ?? 0} creditsLimit={tenant?.sms_limit ?? 500} />
          <NotificationBell urgentItems={urgentItems} strategies={strategies} tenant={tenant} contacts={contacts} />
          <SmsComposeButton contacts={contacts} tenant={tenant} addNotification={addNotification} />
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          CHECKLIST ONBOARDING — visible tant que l'installation
          n'est pas terminée (Lot C)
      ══════════════════════════════════════════════════════ */}
      {(() => {
        const ob = tenant?.settings?.onboarding;
        if (!ob || ob.completed) return null;
        const steps = [
          { key: 'profile',    icon: '🏢', label: 'Ton commerce',       done: !!ob.profile },
          { key: 'contacts',   icon: '📥', label: 'Tes contacts',       done: !!ob.contacts || contacts.length > 0 },
          { key: 'radar_seen', icon: '📡', label: 'Ton radar',          done: !!ob.radar_seen },
          { key: 'first_send', icon: '🚀', label: 'Première action',    done: !!ob.first_send },
        ];
        const doneCount = steps.filter(st => st.done).length;
        if (doneCount === steps.length) return null;
        return (
          <div className="db2-animate-in" style={{ animationDelay: '20ms' }}>
            <div
              onClick={() => navigate('/onboarding')}
              style={{
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', cursor: 'pointer',
                background: '#fff', border: '1.5px solid var(--violet-light, #9B8EFF)',
                borderRadius: 16, padding: '16px 20px',
                boxShadow: '0 6px 20px rgba(90,53,255,.10)',
              }}
            >
              <span style={{ fontSize: 24 }}>🚀</span>
              <div style={{ flex: '1 1 260px' }}>
                <div style={{ fontWeight: 800, fontSize: 14.5 }}>Termine ton installation — {doneCount}/{steps.length}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  {steps.map(st => (
                    <span key={st.key} style={{
                      fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '4px 12px',
                      background: st.done ? '#ECFDF5' : 'var(--bg, #F5F3FF)',
                      color: st.done ? '#065F46' : 'var(--muted, #6B7280)',
                      border: `1px solid ${st.done ? '#A7F3D0' : 'var(--line, #E8E5F4)'}`,
                    }}>
                      {st.icon} {st.label}{st.done ? ' ✓' : ''}
                    </span>
                  ))}
                </div>
              </div>
              <span style={{
                flexShrink: 0, fontWeight: 800, fontSize: 13, color: '#fff',
                background: 'linear-gradient(135deg, #5A35FF, #2563EB)',
                borderRadius: 999, padding: '10px 20px',
              }}>Continuer →</span>
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════
          HERO BIENVENUE — en premier, comme un accueil
      ══════════════════════════════════════════════════════ */}
      <div className="db2-hero db2-animate-in" style={{ animationDelay: '30ms' }}>
        {/* Carte de bienvenue avec génie */}
        <HeroWelcomeCard
          todayCount={todayBdays.length}
          tipsCount={recommendations.length}
          mascot={mascotAiImg}
          autoSentCount={autoSentTodayCount}
          dailyAdvice={recommendations[0] || null}
          strategies={strategies}
          tenant={tenant}
          contactsCount={contacts.length}
          emails30d={emailStats30d?.sent ?? 0}
        />
      </div>

      {/* 🗺️ Parcours d'avancement — où j'en suis vs plein potentiel */}
      <div className="db2-animate-in" style={{ animationDelay: '35ms' }}>
        <ProgressRoadmap />
      </div>

      {/* Profil express + base à nettoyer : FUSIONNÉS dans la timeline ci-dessus
          (ProgressRoadmap) — plus de bandeau jaune séparé, zéro doublon. */}

      {/* ══════════════════════════════════════════════════════
          🧞 LE GÉNIE — prochaine meilleure action (coup d'avance)
          Lit l'état du compte et propose LA suite. S'efface quand
          tout est lancé (place au Radar).
      ══════════════════════════════════════════════════════ */}
      {/* GenieGuide retiré : faisait doublon avec le Radar d'opportunités ci-dessous. */}

      {/* ══════════════════════════════════════════════════════
          📡 RADAR D'OPPORTUNITÉS — le CA qui dort, en images
      ══════════════════════════════════════════════════════ */}
      <div className="db2-animate-in" style={{ animationDelay: '90ms' }}>
        <OpportunityRadar />
      </div>

      {/* 📓 Journal du Génie — la mémoire + la comparaison des actions (rend null si vide) */}
      <div className="db2-animate-in" style={{ animationDelay: '105ms' }}>
        <GenieJournal />
      </div>

      {/* Portes d'entrée « Ton analyse complète » + « Attire de nouveaux clients » retirées :
          doublon avec le Radar d'opportunités (analyse) et la carte 🧲 « Ta base vaut de l'or » (concours). */}

      {/* ══════════════════════════════════════════════════════
          GROUPE ANNIVERSAIRES (aujourd'hui + semaine)
      ══════════════════════════════════════════════════════ */}
      {(todayBdays.length > 0 || weekBdays.length > 0) && (
      <div className="db2-bday-group db2-animate-in" style={{ animationDelay: '200ms' }}>

      {todayBdays.length > 0 && (() => {
        const sentCount = todayBdays.filter(c => {
          const d = c.last_email_sent?.slice(0, 10);
          return d && (d === todayStr || d === bdayThisYear(c)?.slice(0, 10));
        }).length;
        const readCount = todayBdays.filter(c => {
          const d = c.last_email_opened?.slice(0, 10);
          return d && (d === todayStr || d === bdayThisYear(c)?.slice(0, 10));
        }).length;
        return (
          <div id="section-today" className="db2-section-block db2-section-card db2-section-card--today">
            <div className="db2-section-head db2-section-head--today">
              <div className="db2-section-head-left">
                <span className="db2-section-picto">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </span>
                <span className="db2-section-label">{todayBdays.length} ANNIVERSAIRE{todayBdays.length > 1 ? 'S' : ''} AUJOURD'HUI</span>
              </div>
              <Link to="/birthdays" className="db2-section-viewall">
                Voir tout ({todayBdays.length}) →
              </Link>
            </div>
            <div className="db2-bday-rows">
              {todayBdays.map(c => {
                const age     = getAge(c.birth_date);
                const isRound = age !== null && age % 10 === 0;
                const autoSt  = getAutoStatus(c, true);
                const hasCall = needsCallAdvice(c);
                const conseil = getConseilMessage(c);
                return (
                  <div key={c.id} className="db2-bday-row-wrap">
                    <div className="db2-bday-row">
                      {/* Avatar — anneau doré + badge âge pour les âges ronds */}
                      <div className={`db2-avatar-wrap${isRound ? ' db2-avatar-wrap--round' : ''}`}>
                        <div className="db2-avatar" style={getAvatarStyle(c.first_name)}>
                          {(c.first_name||'?').charAt(0)}{(c.last_name||'').charAt(0)}
                        </div>
                        {isRound && <span className="db2-avatar-age">{age}</span>}
                      </div>
                      {/* Identité */}
                      <div className="db2-bday-identity">
                        <div className="db2-bday-fullname">
                          {c.first_name} {c.last_name}
                        </div>
                        <div className="db2-bday-details">
                          {age} ans{c.city ? ` · ${c.city}` : ''}
                          {c.category && <span className="db2-cat-pill">{c.category}</span>}
                        </div>
                        {(c.notes || c.relationship_memory) && (
                          <div className="db2-bday-notes">
                            💬 {c.notes || c.relationship_memory}
                          </div>
                        )}
                      </div>
                      {/* Zone info unifiée : canal + tags */}
                      <div className="db2-bday-info">
                        {/* Canal principal */}
                        <SendChip icon={autoSt.icon} label={autoSt.label} type={autoSt.type} />
                        {/* SMS — affiché si envoyé, échoué, ou prévu (contact a un tel + SMS anniversaire activé) */}
                        {c.phone && (() => {
                          const st = c.birthday_sms_status;
                          const at = c.birthday_sms_sent_at;
                          // Déjà envoyé ou échoué → statut réel
                          if (st === 'sent') {
                            const t = fmtSentTime(at);
                            return <SendChip icon="📱" label={t ? `SMS à ${t}` : 'SMS envoyé'} type="sms" />;
                          }
                          if (st === 'failed') return <SendChip icon="📱" label="SMS échoué" type="failed" />;
                          // Pas encore envoyé → prévu si SMS anniversaire activé
                          if (birthdaySmsEnabled && !c.sms_opted_out) {
                            return <SendChip icon="📱" label="SMS à 7h00" type="sending" />;
                          }
                          return null;
                        })()}
                        {/* Tags discrets — l'âge rond vit sur l'avatar (anneau doré) */}
                        {hasCall && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <InfoTag>📞 Appel</InfoTag>
                          </div>
                        )}
                      </div>
                      {/* Actions */}
                      <div className="db2-bday-btns">
                        {c.phone && (
                          <a href={`tel:${c.phone}`} className="db2-icon-btn" title="Appeler">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.42 2 2 0 0 1 3.6 1.25h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.4A16 16 0 0 0 15.6 16.09l.91-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                          </a>
                        )}
                        {c.phone && (
                          <a href={`sms:${c.phone}?body=${encodeURIComponent(`Bonjour ${c.first_name}, je te souhaite un très bel anniversaire 🎂 !`)}`} className="db2-icon-btn" title="SMS">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                          </a>
                        )}
                        <OpenPreviewBtn id={c.id} />
                      </div>
                    </div>
                    {/* Conseil Appee */}
                    {conseil && (
                      <div className="db2-conseil">
                        <span className="db2-conseil-icon">💡</span>
                        <span className="db2-conseil-text">
                          <strong>Mon conseil : </strong>{conseil}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════
          CETTE SEMAINE
      ══════════════════════════════════════════════════════ */}
      {weekBdays.length > 0 && (() => {
        const todayM = today.getMonth() + 1;
        const todayD = today.getDate();
        const todayIds = new Set(todayBdays.map(c => c.id));
        const weekOnly = weekBdays
          .filter(c => {
            if (!c.birth_date) return false;
            if (todayIds.has(c.id)) return false;
            const [, m, d] = c.birth_date.split('-');
            return !(parseInt(m) === todayM && parseInt(d) === todayD);
          })
          .map(c => {
            const [, m, d] = c.birth_date.split('-');
            const bday = new Date(today.getFullYear(), parseInt(m) - 1, parseInt(d));
            const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const diff = Math.round((bday - todayMidnight) / (1000 * 3600 * 24));
            return { ...c, _bday: bday, _diff: diff };
          })
          .sort((a, b) => a._diff - b._diff);
        if (weekOnly.length === 0) return null;
        return (
          <WeekBdaySection key="week-section" weekOnly={weekOnly} />
        );
      })()}

      </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MES CONSEILS + BASE CLIENTS — deux colonnes
      ══════════════════════════════════════════════════════ */}
      {(() => { return (
      /* « Base clients à nettoyer » a migré dans l'encart jaune du haut (ProfileSpotlight) —
         ici MES CONSEILS occupe toute la largeur, plus de doublon. */
      <div className="db2-mid-cols db2-animate-in" style={{ gridTemplateColumns: '1fr', animationDelay: '280ms' }}>
        {/* MES CONSEILS — pleine largeur */}
        <div id="section-recos" className="db2-section-block db2-section-card db2-section-card--recos db2-mid-col--left">
            <div className="db2-section-head">
              <div className="db2-section-head-left">
                <span className="db2-section-picto">✨</span>
                <span className="db2-section-label">MES CONSEILS</span>
                <span className="db2-section-label-hint">ces moments méritent ta présence humaine</span>
              </div>
            </div>
            {(showAllRecos ? recommendations : recommendations.slice(0, 3)).map((r, i) => (
              <div key={i} className="db2-reco-item">
                <div className="db2-reco-num">{i + 1}</div>
                <div className="db2-reco-body">
                  <div className="db2-reco-title">{r.icon} {r.title}</div>
                  <div className="db2-reco-chips">
                    <span className={`db2-reco-when ${r.whenDiff === 0 ? 'db2-when-today' : r.whenDiff === 1 ? 'db2-when-tomorrow' : 'db2-when-later'}`}>
                      📅 {r.when}
                    </span>
                    {r.actionType && (
                      <span className="db2-reco-chip">{r.actionType === 'Appel' ? '📞' : r.actionType === 'Campagne' ? '📣' : '✉️'} {r.actionType}</span>
                    )}
                  </div>
                  <div className="db2-reco-desc">{r.desc}</div>
                  {r.memory && (
                    <div className="db2-reco-memory">
                      <span>💬</span>
                      <span>{r.memory.length > 120 ? r.memory.slice(0, 120) + '…' : r.memory}</span>
                    </div>
                  )}
                </div>
                <div className="db2-reco-btns">
                  {r.phone && <a href={`tel:${r.phone}`} className="db2-reco-btn db2-reco-btn-call">📞 Appeler</a>}
                  {r.actionType === 'Campagne' && r.preset ? (
                    <button
                      className="db2-reco-btn"
                      onClick={() => navigate('/campaigns/new', { state: { startStep: 2, preset: r.preset } })}
                    >📣 Créer</button>
                  ) : (
                    <Link to={r.link} className="db2-reco-btn">{r.actionType === 'Campagne' ? '📣 Créer' : '👤 Fiche'}</Link>
                  )}
                </div>
              </div>
            ))}
            {recommendations.length > 3 && (
              <button className="db2-reco-toggle" onClick={() => setShowAllRecos(v => !v)}>
                {showAllRecos ? '↑ Réduire' : `↓ Voir toutes mes actions (${recommendations.length})`}
              </button>
            )}
          </div>
      </div>
      ); })()}


      {/* ── Calendrier marketing (EN DEHORS de l'encart violet) ── */}
      {/* ── Footer ── */}
      <div className="db2-footer">
        💜 Tu fais grandir tes relations chaque jour. Bravo {firstName} ! 💜
      </div>

      {showStrategicDash && (
        <StrategicDashboard dormant={[]} vip={[]} onClose={() => setShowStrategicDash(false)} />
      )}

      {/* ── Modal Vue Générale Marketing ── */}
      {showMarketingOverview && (() => {
        const ms = calcMarketingStatus(companyProfile);
        const scoreColor = ms.score >= 70 ? '#16A34A' : ms.score >= 40 ? '#D97706' : '#EF4444';
        const scoreBg    = ms.score >= 70 ? '#F0FDF4' : ms.score >= 40 ? '#FFFBEB' : '#FEF2F2';
        const dayMsg = MARKETING_MOTIVATIONS[new Date().getDay() % MARKETING_MOTIVATIONS.length];
        const motivMsg = dayMsg(ms.score, firstName ? ` ${firstName}` : '');
        const auditScore = companyProfile?.marketing_audit?.overall_score;
        const auditDate  = companyProfile?.marketing_audit?.audited_at;
        return (
          <div
            onClick={e => e.target === e.currentTarget && setShowMarketingOverview(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(10,5,30,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(4px)' }}
          >
            <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 32px 80px rgba(90,53,255,0.2)', display: 'flex', flexDirection: 'column' }}>

              {/* Header */}
              <div style={{ background: 'linear-gradient(135deg, #5A35FF 0%, #7C3AED 100%)', borderRadius: '20px 20px 0 0', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>🎯 Vue marketing globale</div>
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginTop: 3 }}>{motivMsg}</div>
                </div>
                <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.12)', borderRadius: 14, padding: '10px 16px', flexShrink: 0 }}>
                  <div style={{ fontSize: 28, fontWeight: 900, color: ms.score >= 70 ? '#4ade80' : ms.score >= 40 ? '#FCD34D' : '#FCA5A5' }}>{ms.score}%</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>PROFIL IA</div>
                </div>
                <button onClick={() => setShowMarketingOverview(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', fontSize: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>

              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Score bar */}
                <div style={{ background: scoreBg, border: `1.5px solid ${scoreColor}30`, borderRadius: 12, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, fontWeight: 700 }}>
                    <span style={{ color: scoreColor }}>Complétude du profil</span>
                    <span style={{ color: scoreColor }}>{ms.score}/100</span>
                  </div>
                  <div style={{ height: 8, background: '#E5E7EB', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${ms.score}%`, background: `linear-gradient(90deg, ${scoreColor}, ${scoreColor}bb)`, borderRadius: 999, transition: 'width 0.8s ease' }} />
                  </div>
                  {auditScore != null && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#6B7280' }}>
                      📊 Dernier audit : <strong style={{ color: auditScore >= 70 ? '#16A34A' : auditScore >= 40 ? '#D97706' : '#EF4444' }}>{auditScore}/100</strong>
                      {auditDate && <span> · {new Date(auditDate).toLocaleDateString('fr-CH')}</span>}
                    </div>
                  )}
                </div>

                {/* Two columns: goods / improvements */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Ce qui va bien */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#16A34A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>✅ Ce qui va bien</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {ms.goods.length > 0 ? ms.goods.map((g, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '7px 10px', fontSize: 12, color: '#15803D', fontWeight: 500 }}>
                          <span>{g.icon}</span><span>{g.text}</span>
                        </div>
                      )) : (
                        <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', padding: '8px 0' }}>Commence à remplir ton profil pour voir tes points forts !</div>
                      )}
                    </div>
                  </div>

                  {/* À améliorer */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#D97706', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>🎯 À améliorer</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {ms.improvements.length > 0 ? ms.improvements.map((imp, i) => (
                        <button key={i} onClick={() => { navigate(imp.link || '/settings?tab=entreprise'); setShowMarketingOverview(false); }} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '7px 10px', fontSize: 12, color: '#92400E', fontWeight: 500, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                          <span>{imp.icon}</span><span style={{ flex: 1 }}>{imp.text}</span><span style={{ fontSize: 11, color: '#D97706', fontWeight: 700 }}>Corriger →</span>
                        </button>
                      )) : (
                        <div style={{ fontSize: 12, color: '#16A34A', fontStyle: 'italic', padding: '8px 0' }}>🏆 Profil complet — tu es au sommet !</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', paddingTop: 4 }}>
                  <button onClick={() => { navigate('/settings?tab=entreprise'); setShowMarketingOverview(false); }} style={{ background: 'linear-gradient(135deg, #5A35FF, #7C3AED)', color: '#fff', border: 'none', borderRadius: 12, padding: '11px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(90,53,255,0.3)' }}>
                    ✏️ Améliorer mon profil
                  </button>
                  {auditScore == null && (
                    <button onClick={() => { navigate('/settings?tab=entreprise'); setShowMarketingOverview(false); }} style={{ background: '#F5F3FF', color: '#5A35FF', border: '1.5px solid #C4B5FD', borderRadius: 12, padding: '11px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                      📊 Lancer un audit
                    </button>
                  )}
                </div>

              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Top Opportunités (§34.4 CDC) ─────────────────────────────────────────────
function TopOpportunites({ contacts }) {
  const top = useMemo(() => {
    return [...contacts]
      .filter(c => (c.opportunity_score || 0) > 20 && c.status === 'active')
      .sort((a, b) => (b.opportunity_score || 0) - (a.opportunity_score || 0))
      .slice(0, 4);
  }, [contacts]);

  if (top.length === 0) return null;

  const scoreColor = s => s >= 70 ? '#5A35FF' : s >= 50 ? '#F59E0B' : '#10B981';

  return (
    <Card>
      <CardHeader title="🎯 Top Opportunités" subtitle="Contacts à contacter en priorité" />
      <SectionHint>
        Appee repère pour toi les clients à contacter <strong>maintenant</strong> (ceux qui rapportent le plus, ceux qui s'endorment). Tu ne cherches pas dans ta base — tu agis directement sur ce qui rapporte.
      </SectionHint>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {top.map(c => {
          const score = c.opportunity_score || 0;
          const color = scoreColor(score);
          const hints = [];
          if (c.birth_date) {
            const today = new Date();
            const [, m, d] = c.birth_date.split('-').map(Number);
            let bday = new Date(today.getFullYear(), m - 1, d);
            if (bday < today) bday.setFullYear(today.getFullYear() + 1);
            const diff = Math.round((bday - today) / (1000 * 3600 * 24));
            if (diff <= 30) hints.push(`🎂 dans ${diff}j`);
          }
          if (c.category === 'VIP') hints.push('⭐ VIP');
          const initials = `${(c.first_name || '?').charAt(0)}${(c.last_name || '').charAt(0)}`;
          return (
            <Link key={c.id} to={`/contacts/${c.id}`} style={{ textDecoration: 'none' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 10,
                border: `1px solid ${color}25`, background: `${color}08`,
                transition: 'box-shadow .15s',
              }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                  background: `${color}20`, color, fontWeight: 800, fontSize: 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{initials}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1F2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.first_name} {c.last_name}
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 1 }}>
                    {hints.length > 0 ? hints.join(' · ') : c.city || ''}
                  </div>
                </div>
                <div style={{ textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 16, color }}>{score}</div>
                  <div style={{ fontSize: 9, color: '#9CA3AF', textTransform: 'uppercase' }}>score</div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      <Link to="/contacts?sort=opportunity_score&dir=desc" style={{ display: 'block', textAlign: 'center', marginTop: 10, fontSize: 12, color: 'var(--violet)', fontWeight: 600, textDecoration: 'none' }}>
        Voir tous les contacts →
      </Link>
    </Card>
  );
}

// ── Gamification douce (§32 CDC) ─────────────────────────────────────────────
const LEVELS = [
  { min: 0,   max: 9,   name: 'Créateur de lien',     icon: '🌱', color: '#6B7280', bg: '#F9FAFB', border: '#D1D5DB' },
  { min: 10,  max: 49,  name: 'Relation active',       icon: '💙', color: '#3B82F6', bg: '#EFF6FF', border: '#93C5FD' },
  { min: 50,  max: 199, name: 'Good Energy Sender',    icon: '⚡', color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7' },
  { min: 200, max: 499, name: 'Ambassadeur client',    icon: '🚀', color: '#5A35FF', bg: '#EDE9FF', border: '#A78BFA' },
  { min: 500, max: Infinity, name: 'Génie des relations', icon: '🌟', color: '#F59E0B', bg: '#FEF3C7', border: '#FDE68A' },
];

function getLevel(emailsSent) {
  return LEVELS.find(l => emailsSent >= l.min && emailsSent <= l.max) ?? LEVELS[0];
}

// ── Badges definitions (§32 CDC) ─────────────────────────────────────────────
const BADGES = [
  { id: 'first10',  icon: '🚀', label: '10 attentions envoyées',   check: (u) => (u?.emails_used ?? 0) >= 10 },
  { id: 'noubli',   icon: '🎂', label: 'Aucun anniversaire oublié', check: (u) => (u?.birthdays_sent ?? 0) >= 5 },
  { id: 'positif',  icon: '💛', label: 'Premier retour positif',    check: (u) => (u?.feedbacks_received ?? 0) >= 1 },
  { id: 'dormant',  icon: '💤', label: 'Client dormant réveillé',   check: (u) => (u?.reactivations ?? 0) >= 1 },
  { id: 'enrichi',  icon: '📋', label: 'Base enrichie',             check: (u) => (u?.contacts_used ?? 0) >= 10 },
  { id: 'local',    icon: '📍', label: 'Campagne locale lancée',    check: (u) => (u?.campaigns_local ?? 0) >= 1 },
  { id: 'vip',      icon: '⭐', label: 'VIP remercié',              check: (u) => (u?.vip_contacted ?? 0) >= 1 },
  { id: 'cent',     icon: '💯', label: '100 attentions envoyées',   check: (u) => (u?.emails_used ?? 0) >= 100 },
];

function GamificationCard({ usage, stats }) {
  const sent  = usage?.emails_used ?? 0;
  const level = getLevel(sent);
  const next  = LEVELS[LEVELS.indexOf(level) + 1];
  const pct   = next
    ? Math.min(Math.round(((sent - level.min) / (next.min - level.min)) * 100), 100)
    : 100;
  const toNext = next ? next.min - sent : 0;

  const earnedBadges = BADGES.filter(b => b.check(usage));

  return (
    <Card>
      <div style={{ textAlign: 'center', padding: '4px 0 10px' }}>
        <div style={{ fontSize: 32, marginBottom: 4 }}>{level.icon}</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: level.color }}>{level.name}</div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
          {sent === 0 ? 'Prêt à créer ta première relation ✨' : `${sent.toLocaleString()} message${sent > 1 ? 's' : ''} envoyé${sent > 1 ? 's' : ''}`}
        </div>
      </div>
      {next && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>
            <span>Niveau actuel</span>
            <span><strong>{toNext}</strong> pour "{next.name} {next.icon}"</span>
          </div>
          <div style={{ height: 7, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: level.color, borderRadius: 4, transition: 'width .6s' }} />
          </div>
        </div>
      )}
      <div className="appee-tip" style={{ marginTop: 4 }}>
        {sent === 0
          ? `🌱 Lance ton premier message pour débuter ton aventure relationnelle !`
          : next
            ? `💪 Continue sur ta lancée — encore ${toNext} envoi${toNext > 1 ? 's' : ''} pour passer ${next.name} !`
            : `🌟 Tu as atteint le niveau maximum. Tu es un vrai Génie des relations !`}
      </div>
      {earnedBadges.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            🏅 Badges débloqués — {earnedBadges.length}/{BADGES.length}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {earnedBadges.map(b => (
              <div key={b.id} title={b.label} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: '#EDE9FF', border: '1px solid #A78BFA',
                borderRadius: 99, padding: '3px 9px', fontSize: 11, fontWeight: 700, color: '#5A35FF',
              }}>
                {b.icon} {b.label}
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          📊 Tes stats
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {[
            { label: 'Attentions envoyées', value: usage?.emails_used ?? 0 },
            { label: 'Anniversaires',        value: usage?.birthdays_sent ?? 0 },
            { label: 'Feedbacks reçus',      value: usage?.feedbacks_received ?? 0 },
            { label: 'Contacts enrichis',    value: usage?.contacts_used ?? 0 },
          ].map(s => (
            <div key={s.label} style={{
              background: 'var(--bg2, #F9FAFB)', border: '1px solid var(--line)', borderRadius: 8,
              padding: '8px 10px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1F2937' }}>{s.value.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ── Week Birthday Carousel v2 (pagination avec flèches) ─────────────────────
function WeekBdayCardV2({ c }) {
  const age = getAge(c.birth_date);
  const dayAbbr = format(c._bday, 'EEE', { locale: fr }).toUpperCase().slice(0, 3);
  const dayNum  = format(c._bday, 'd');
  const dateStr = format(c._bday, 'd MMM', { locale: fr });
  const { openPreview } = useContactPreview();
  return (
    <button onClick={() => openPreview(c.id)} className="db2-bday-card-v2" style={{ cursor:'pointer', textAlign:'left', font:'inherit' }}>
      <div className="db2-bday-v2-daydate">
        <span className="db2-bday-v2-day">{dayAbbr}</span>
        <span className="db2-bday-v2-num">{dayNum}</span>
      </div>
      <div className="db2-avatar db2-avatar-md" style={getAvatarStyle(c.first_name)}>
        {(c.first_name||'?').charAt(0)}{(c.last_name||'').charAt(0)}
      </div>
      <div className="db2-bday-v2-info">
        <div className="db2-bday-v2-name">{c.first_name} {(c.last_name||'').charAt(0)}.</div>
        <div className="db2-bday-v2-meta">{age} ans · {dateStr}</div>
        {c.category && <span className="db2-bday-v2-badge">{c.category}</span>}
      </div>
      <span className="db2-bday-v2-gift">🎁</span>
    </button>
  );
}

function WeekBdaySection({ weekOnly }) {
  const [page, setPage] = useState(0);
  const perPage = 3;
  const totalPages = Math.ceil(weekOnly.length / perPage);
  const visible = weekOnly.slice(page * perPage, (page + 1) * perPage);
  return (
    <div id="section-week" className="db2-bday-section">
      <div className="db2-bday-section-head">
        <div className="db2-bday-section-left">
          <div className="db2-bday-section-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <span className="db2-bday-section-title">{weekOnly.length} ANNIVERSAIRE{weekOnly.length > 1 ? 'S' : ''} CETTE SEMAINE</span>
        </div>
        <Link to="/birthdays?tab=week" className="db2-bday-section-viewall">Voir tout ({weekOnly.length}) →</Link>
      </div>
      <div className="db2-bday-carousel">
        {totalPages > 1 && (
          <button className="db2-bday-arrow db2-bday-arrow--prev" onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
        )}
        <div className="db2-bday-cards-row">
          {visible.map(c => <WeekBdayCardV2 key={c.id} c={c} />)}
        </div>
        {totalPages > 1 && (
          <button className="db2-bday-arrow db2-bday-arrow--next" onClick={() => setPage(p => Math.min(totalPages-1, p+1))} disabled={page === totalPages-1}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        )}
      </div>
      {totalPages > 1 && (
        <div className="db2-bday-dots">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button key={i} className={`db2-bday-dot${i === page ? ' db2-bday-dot--active' : ''}`} onClick={() => setPage(i)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sparkle SVG ──────────────────────────────────────────────────────────────
function Sparkle({ size = 18, color = '#FFC400', style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
      <path d="M12 2 L13.5 9.5 L21 12 L13.5 14.5 L12 22 L10.5 14.5 L3 12 L10.5 9.5 Z"/>
    </svg>
  );
}

// ── Hero Welcome Card ────────────────────────────────────────────────────────
function HeroWelcomeCard({ todayCount, tipsCount, mascot, autoSentCount, dailyAdvice, strategies = [], tenant = null, contactsCount = 0, emails30d = 0 }) {
  const animTodayCount = useCountUp(todayCount);
  const animTipsCount  = useCountUp(tipsCount);

  // Stratégie en cours
  const activeStrat  = strategies[0] || null;
  const stratSt      = activeStrat ? getStratStatus(activeStrat) : { pct: 0, done: 0, total: 0, isBehind: false };
  const animStratPct = useCountUp(stratSt.pct);

  const adviceRaw = dailyAdvice?.desc || dailyAdvice?.text || 'Un simple message personnalisé peut recréer du lien avec un client silencieux.';
  const typewriterAdvice = useTypewriter(adviceRaw, 18, 1400);

  // Détection plan limité (starter / free ou quota contacts très bas)
  const isFreePlan = tenant?.plan === 'free' || tenant?.plan === 'starter' ||
    (!tenant?.plan && (tenant?.contacts_limit ?? 999) <= 50);

  const adviceHref = dailyAdvice?.link || dailyAdvice?.href || '/recommendations';
  // Astuce Appee rotative (mène au Guide) — stable le temps de l'affichage
  const heroAstuce = useMemo(() => ASTUCES[Math.floor(Math.random() * ASTUCES.length)], []);

  return (
    <div className="db2-welcome-card">
      <div className="db2-welcome-grid">

        {/* ── Colonne gauche ── */}
        <div className="db2-welcome-content">
          {dailyAdvice ? (
            <>
              <div className="db2-welcome-eyebrow">
                🧞 {dailyAdvice.whenDiff === 0 ? "Aujourd'hui, commence par" : `À préparer — ${dailyAdvice.when}`}
              </div>
              <h2 className="db2-welcome-action">
                {(() => {
                  const n = dailyAdvice.name;
                  if (n && dailyAdvice.title.includes(n)) {
                    const [before, after] = dailyAdvice.title.split(n);
                    return <>{before}<span className="db2-welcome-name">{n}</span>{after}</>;
                  }
                  return dailyAdvice.title;
                })()}
              </h2>
              {(dailyAdvice.age != null || dailyAdvice.city || dailyAdvice.memory) && (
                <div className="db2-welcome-meta">
                  {dailyAdvice.age != null && <span className="db2-welcome-mchip">🎂 {dailyAdvice.age} ans</span>}
                  {dailyAdvice.city && <span className="db2-welcome-mchip">📍 {dailyAdvice.city}</span>}
                  {dailyAdvice.memory && <span className="db2-welcome-mchip db2-welcome-mchip-memo">💬 {dailyAdvice.memory}</span>}
                </div>
              )}
              {dailyAdvice.desc && (
                <p className="db2-welcome-action-desc">
                  {dailyAdvice.desc.length > 150 ? dailyAdvice.desc.slice(0, 150).replace(/\s+\S*$/, '') + '…' : dailyAdvice.desc}
                </p>
              )}
              <div className="db2-welcome-cta-row">
                {dailyAdvice.phone && (
                  <a href={`tel:${dailyAdvice.phone}`} className="db2-welcome-cta">📞 Appeler maintenant</a>
                )}
                <a href="#section-recos" className={`db2-welcome-cta${dailyAdvice.phone ? ' db2-welcome-cta-ghost' : ''}`}>
                  {dailyAdvice.phone ? 'Voir mes conseils' : 'Voir mes conseils du jour'}
                </a>
                {(() => {
                  const e = dailyAdvice.actionType === 'Appel' ? '≈ 4 min'
                    : dailyAdvice.actionType === 'E-mail' ? '≈ 2 min'
                    : dailyAdvice.actionType === 'Campagne' ? '≈ 5 min' : null;
                  return e ? <span className="db2-welcome-eta">⏱ {e}</span> : null;
                })()}
              </div>
            </>
          ) : (
            <>
              <div className="db2-welcome-eyebrow">🧞 Rien d'urgent</div>
              <h2 className="db2-welcome-action">Tout roule aujourd'hui ✨</h2>
              <p className="db2-welcome-action-desc">Je veille sur ta base et je t'alerte au bon moment — profites-en. 💜</p>
            </>
          )}


          {/* 3 mini cartes cliquables */}
          <div className="db2-welcome-mini-cards">

            {/* ── Carte 1 : Anniversaires aujourd'hui ── */}
            <a href="#section-today" className="db2-mini-card db2-mini-card-purple db2-mini-card-btn">
              <div className="db2-mini-card-top-row">
                <div className="db2-mini-card-icon-wrap db2-mini-card-icon-purple">
                  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 128 128">
                    <circle cx="64" cy="64" r="48" fill="#F4EEFF"/>
                    <path d="M36 62h56c10 0 18 8 18 18v22H18V80c0-10 8-18 18-18z" fill="#7A5CF5"/>
                    <path d="M22 82c12 9 24 9 36 0 12 9 24 9 36 0 6 4 11 6 16 6v14H18V84c1 0 3-1 4-2z" fill="#FFB61A"/>
                    <rect x="58" y="32" width="12" height="28" rx="6" fill="#fff"/>
                    <path d="M64 22c8 7 8 14 0 20-8-6-8-13 0-20z" fill="#FFB61A"/>
                  </svg>
                </div>
                <div className="db2-mini-card-val">{animTodayCount}</div>
              </div>
              <div className="db2-mini-card-title">{`anniversaire${todayCount !== 1 ? 's' : ''} aujourd'hui`}</div>
              <p className="db2-mini-card-text">
                {todayCount === 0 ? 'Tout est sous contrôle — profites-en !'
                  : autoSentCount > 0 ? `${autoSentCount} message${autoSentCount > 1 ? 's' : ''} envoyé${autoSentCount > 1 ? 's' : ''} automatiquement.`
                  : 'Les messages sont prêts ou envoyés.'}
              </p>
              <div className="db2-mini-card-arrow db2-mini-card-arrow-purple">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M15 6l6 6-6 6"/></svg>
              </div>
            </a>

            {/* ── Carte 2 : Stratégie (3 états) ── */}
            {activeStrat ? (
              /* État A — Stratégie active */
              <a href="/strategy" className="db2-mini-card db2-mini-card-strat db2-mini-card-btn">
                <div className="db2-mini-card-top-row">
                  <div className="db2-mini-card-icon-wrap db2-mini-card-icon-strat">
                    <span style={{ fontSize: 28, lineHeight: 1 }}>{activeStrat.icon || '🧠'}</span>
                  </div>
                  <div className="db2-mini-card-val" style={{ fontSize: 22 }}>{animStratPct}%</div>
                </div>
                <div className="db2-mini-card-title">stratégie en cours</div>
                {/* Barre de progression */}
                <div style={{ height: 4, background: 'rgba(90,53,255,.12)', borderRadius: 10, margin: '4px 0 6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: stratSt.isBehind ? '#EF4444' : stratSt.pct >= 70 ? '#10B981' : '#5A35FF',
                    width: `${stratSt.pct}%`, borderRadius: 10, transition: 'width .6s ease' }} />
                </div>
                <p className="db2-mini-card-text">
                  {activeStrat.title?.slice(0, 28)}{activeStrat.title?.length > 28 ? '…' : ''}
                  <br/>
                  <span style={{ color: stratSt.isBehind ? '#EF4444' : '#10B981', fontWeight: 700 }}>
                    {stratSt.isBehind ? '⚠️ En retard' : `${stratSt.done}/${stratSt.total} actions ✓`}
                  </span>
                </p>
                <div className="db2-mini-card-arrow db2-mini-card-arrow-strat">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M15 6l6 6-6 6"/></svg>
                </div>
              </a>
            ) : isFreePlan ? (
              /* État C — Plan limité : teaser aspirationnel */
              <a href="/settings?tab=plan" className="db2-mini-card db2-mini-card-upsell db2-mini-card-btn">
                <div className="db2-mini-card-top-row">
                  <div className="db2-mini-card-icon-wrap db2-mini-card-icon-upsell">
                    <span style={{ fontSize: 26, lineHeight: 1 }}>✨</span>
                  </div>
                  <div className="db2-mini-card-val" style={{ fontSize: 13, fontWeight: 800, color: '#D97706', letterSpacing: '-.01em' }}>Pro</div>
                </div>
                <div className="db2-mini-card-title">Stratégie sur-mesure</div>
                <p className="db2-mini-card-text" style={{ fontSize: 11 }}>
                  📅 Plan semaine par semaine<br />
                  🤖 Personnalisé pour ton secteur<br />
                  📈 Suivi de progression en live
                </p>
                <div className="db2-mini-card-arrow db2-mini-card-arrow-upsell">
                  Voir ce que ça donne →
                </div>
              </a>
            ) : (
              /* État B — Accès OK, aucune stratégie créée */
              <a href="/strategy" className="db2-mini-card db2-mini-card-green db2-mini-card-btn">
                <div className="db2-mini-card-top-row">
                  <div className="db2-mini-card-icon-wrap db2-mini-card-icon-green">
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 128 128">
                      <circle cx="64" cy="64" r="48" fill="#ECFDF5"/>
                      <path d="M64 30c-2 0-4 1-5 3L40 73c-2 4 1 9 5 9h38c4 0 7-5 5-9L69 33c-1-2-3-3-5-3z" fill="#10B981"/>
                      <circle cx="64" cy="85" r="5" fill="#fff"/>
                      <rect x="61" y="52" width="6" height="24" rx="3" fill="#fff"/>
                    </svg>
                  </div>
                  <div className="db2-mini-card-val" style={{ fontSize: 20 }}>→</div>
                </div>
                <div className="db2-mini-card-title">Lance ta stratégie</div>
                <p className="db2-mini-card-text">
                  Appee crée ton plan d'actions marketing en 2 minutes, adapté à ton activité.
                </p>
                <div className="db2-mini-card-arrow db2-mini-card-arrow-green">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M15 6l6 6-6 6"/></svg>
                </div>
              </a>
            )}

            {/* ── Carte 3 : Conseils utiles ── */}
            <a href="#section-recos" className="db2-mini-card db2-mini-card-orange db2-mini-card-btn">
              <div className="db2-mini-card-top-row">
                <div className="db2-mini-card-icon-wrap db2-mini-card-icon-orange">
                  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 128 128">
                    <circle cx="64" cy="64" r="48" fill="#FFF3DD"/>
                    <path d="M64 25c-18 0-33 14-33 32 0 12 6 22 16 28v7c0 5 4 9 9 9h16c5 0 9-4 9-9v-7c10-6 16-16 16-28 0-18-15-32-33-32z" fill="#FFB61A"/>
                    <path d="M52 106h24" stroke="#1F1646" strokeWidth="7" strokeLinecap="round"/>
                    <path d="M55 88h18" stroke="#fff" strokeWidth="6" strokeLinecap="round" opacity=".75"/>
                  </svg>
                </div>
                <div className="db2-mini-card-val">{animTipsCount}</div>
              </div>
              <div className="db2-mini-card-title">{`conseil${tipsCount !== 1 ? 's' : ''} utile${tipsCount !== 1 ? 's' : ''} aujourd'hui`}</div>
              <p className="db2-mini-card-text">
                {tipsCount === 0 ? 'Aucune action urgente — tout roule !'
                  : 'Ta présence peut créer plus de valeur sur certains contacts.'}
              </p>
              <div className="db2-mini-card-arrow db2-mini-card-arrow-orange">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M15 6l6 6-6 6"/></svg>
              </div>
            </a>

          </div>
        </div>

        {/* ── Colonne droite : génie + décos ── */}
        <div className="db2-welcome-mascot-wrap">
          {/* Fond dégradé doux */}
          <div className="db2-welcome-bg-blob" />

          {/* Sparkles */}
          <Sparkle size={22} color="#FFC400" style={{ position:'absolute', top:'8%',  left:'38%',  animation:'db2-sparkle 2.2s ease-in-out infinite', zIndex:3 }} />
          <Sparkle size={14} color="#FFC400" style={{ position:'absolute', top:'18%', left:'55%',  animation:'db2-sparkle 2.8s ease-in-out infinite .4s', zIndex:3 }} />
          <Sparkle size={18} color="#FFC400" style={{ position:'absolute', top:'42%', left:'10%',  animation:'db2-sparkle 3s ease-in-out infinite .8s', zIndex:3 }} />
          <Sparkle size={12} color="#FFC400" style={{ position:'absolute', bottom:'20%', right:'8%', animation:'db2-sparkle 2.5s ease-in-out infinite 1.2s', zIndex:3 }} />

          {/* Déco message bubble */}
          <div className="db2-deco-chat">
            <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 128 128">
              <rect x="18" y="28" width="92" height="66" rx="22" fill="#fff" opacity=".9"/>
              <rect x="18" y="28" width="92" height="66" rx="22" fill="none" stroke="#D8CCFF" strokeWidth="4"/>
              <path d="M44 94l-17 16v-22" fill="#fff" stroke="#D8CCFF" strokeWidth="4" strokeLinejoin="round"/>
              <circle cx="48" cy="61" r="6" fill="#7A5CF5"/>
              <circle cx="64" cy="61" r="6" fill="#7A5CF5"/>
              <circle cx="80" cy="61" r="6" fill="#7A5CF5"/>
            </svg>
          </div>

          {/* Déco calendrier */}
          <div className="db2-deco-calendar">
            <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 128 128">
              <rect x="20" y="24" width="88" height="84" rx="22" fill="#fff" opacity=".9"/>
              <rect x="20" y="24" width="88" height="84" rx="22" fill="none" stroke="#D8CCFF" strokeWidth="4"/>
              <rect x="20" y="44" width="88" height="16" fill="#7A5CF5" opacity=".18"/>
              <path d="M42 18v22M86 18v22" stroke="#7A5CF5" strokeWidth="8" strokeLinecap="round"/>
              <g fill="#7A5CF5" opacity=".88">
                <rect x="39" y="70" width="10" height="10" rx="3"/>
                <rect x="59" y="70" width="10" height="10" rx="3"/>
                <rect x="79" y="70" width="10" height="10" rx="3"/>
                <rect x="39" y="90" width="10" height="10" rx="3"/>
                <rect x="59" y="90" width="10" height="10" rx="3"/>
              </g>
            </svg>
          </div>

          {/* Déco envelope */}
          <div className="db2-deco-mail">
            <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 128 128">
              <rect x="18" y="32" width="92" height="64" rx="18" fill="#fff" opacity=".9"/>
              <rect x="18" y="32" width="92" height="64" rx="18" fill="none" stroke="#D8CCFF" strokeWidth="4"/>
              <path d="M24 42l40 32 40-32" fill="none" stroke="#7A5CF5" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" opacity=".85"/>
              <path d="M26 90l28-25M102 90L74 65" fill="none" stroke="#7A5CF5" strokeWidth="5" strokeLinecap="round" opacity=".35"/>
            </svg>
            <div className="db2-deco-mail-dot" />
          </div>

          {/* Déco téléphone */}
          <div className="db2-deco-phone">
            <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 128 128">
              <circle cx="64" cy="64" r="48" fill="#FFF3DD"/>
              <path d="M47 35c5-4 11-3 14 3l6 13c2 4 1 8-3 11l-5 4c6 11 15 20 27 27l5-5c3-3 8-4 12-2l13 6c6 3 7 9 3 14l-7 9c-4 5-11 7-18 5-37-11-62-36-73-73-2-7 0-14 5-18l9-7z" fill="#FF9800"/>
            </svg>
          </div>

          {/* Mascotte */}
          <img src={mascot} alt="Appee" className="db2-welcome-mascot" />
        </div>

      </div>

      {/* (Barre de stats supprimée 23.07 — « elle pollue » : le solde SMS vit
          dans la popup SMS, le compteur contacts dans la barre du haut) */}

      {/* Astuce Appee — rotative, mène au Guide (le « manuel » de l'app) */}
      <Link to="/astuces" className="db2-hero-astuce">
        <span className="db2-hero-astuce-ic">💡</span>
        <span className="db2-hero-astuce-txt">
          <b>Astuce Appee</b> · {heroAstuce.title}
        </span>
        <span className="db2-hero-astuce-cta">Voir le Guide →</span>
      </Link>

    </div>
  );
}

// ── Mission du jour — hero encart avec mascot + carousel ─────────────────────
function MissionDuJour({ todayBdays, dormantCount, missingEmail, stats, aiMission, mascot }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const timerRef = useRef(null);

  const getLocalAge = contactAge;

  // Build slides
  const missions = useMemo(() => {
    if (aiMission) {
      return [{ ...aiMission, color: '#5A35FF', border: 'rgba(196,181,253,.6)', bg: 'rgba(237,233,255,.85)', featured: null, isSpecial: false, isBday: false }];
    }
    if (todayBdays.length > 0) {
      return todayBdays.map(c => {
        const age     = getLocalAge(c.birth_date);
        const isRound = age && age % 10 === 0;
        const isVip   = c.category === 'VIP';
        const isSpecial = isRound || isVip;
        let body = '';
        if (isRound && isVip)
          body = `${c.first_name} fête ses ${age} ans aujourd'hui — et c'est un VIP. J'ai préparé un message sur-mesure, il part ce matin. Une attention comme celle-là, ça ne s'oublie pas 🌟`;
        else if (isRound)
          body = `${c.first_name} fête ses ${age} ans — un cap symbolique ! J'ai soigné le message, il est prêt à partir. Ces anniversaires-là, on s'en souvient toujours 🎯`;
        else if (isVip)
          body = `${c.first_name} fête son anniversaire aujourd'hui — et c'est un client VIP. J'envoie le message ce matin, ne t'inquiète pas. Une attention bien placée renforce tout 💙`;
        else
          body = `${c.first_name}${age ? ` (${age} ans)` : ''} fête son anniversaire aujourd'hui. J'ai tout préparé — le message part ce matin à 7h. Toi, tu n'as rien à faire 🎂`;
        return {
          color: isSpecial ? '#D97706' : '#6B7280',
          bg: isSpecial ? 'linear-gradient(135deg, #FFFBEB, #FEF3C7)' : 'rgba(255,255,255,.92)',
          border: isSpecial ? 'rgba(252,211,77,.8)' : 'rgba(255,255,255,.97)',
          title: `${isRound ? '🎯 ' : '🎂 '}${c.first_name} fête ${isRound ? `ses ${age} ans` : 'son anniversaire'} aujourd'hui`,
          body,
          cta: "Voir l'encart anniversaire ↓",
          link: '#section-today',
          featured: c,
          isSpecial,
          isBday: true,
        };
      });
    }
    if (dormantCount > 3) {
      return [{
        color: '#5A35FF',
        bg: 'rgba(237,233,255,.85)', border: 'rgba(167,139,250,.5)',
        title: `J'ai repéré ${dormantCount} contacts silencieux depuis 12 mois`,
        body: `Une campagne de réactivation bien ciblée en récupère 20–30 % en moyenne. Je prépare le modèle, tu n'as qu'à valider. 💤`,
        cta: 'Je veux les récupérer →', link: '/campaigns/new', featured: null, isSpecial: false, isBday: false,
      }];
    }
    if (missingEmail > 0) {
      return [{
        color: '#059669',
        bg: 'rgba(255,255,255,.92)', border: 'rgba(255,255,255,.97)',
        title: `J'ai trouvé ${missingEmail} fiche${missingEmail > 1 ? 's' : ''} sans e-mail`,
        body: `Ces clients existent mais je ne peux pas leur écrire — il manque leur adresse. En l'ajoutant, je pourrai enfin m'occuper d'eux comme des autres. C'est rapide à corriger 👇`,
        cta: 'Compléter les fiches →', link: '/contacts?noEmail=1', featured: null, isSpecial: false, isBday: false,
      }];
    }
    return [{
      color: '#5A35FF',
      bg: 'rgba(237,233,255,.85)', border: 'rgba(196,181,253,.6)',
      title: 'Ta base est au top — je veille sur tout ✅',
      body: `Pas d'action urgente aujourd'hui. C'est le bon moment pour lancer une campagne saisonnière ou gâter tes clients VIP. Je suis prêt quand tu l'es 💪`,
      cta: 'Créer une campagne →', link: '/campaigns', featured: null, isSpecial: false, isBday: false,
    }];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayBdays, dormantCount, missingEmail, aiMission]);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (missions.length <= 1) return;
    timerRef.current = setInterval(() => setCurrentIdx(i => (i + 1) % missions.length), 5000);
    return () => clearInterval(timerRef.current);
  }, [missions.length]);

  useEffect(() => { setCurrentIdx(0); }, [missions]);

  const goTo = (idx) => {
    setCurrentIdx(idx);
    clearInterval(timerRef.current);
    if (missions.length > 1) timerRef.current = setInterval(() => setCurrentIdx(i => (i + 1) % missions.length), 5000);
  };
  const goPrev = () => goTo((currentIdx - 1 + missions.length) % missions.length);
  const goNext = () => goTo((currentIdx + 1) % missions.length);

  const isCarousel = missions.length > 1;
  const cur = missions[currentIdx] || missions[0];

  return (
    <div className="db2-hmission-wrap">
      {/* Encart card — full width, no mascot */}
      <div
        className={`db2-hmission-card${isCarousel ? ' db2-hmission-card--carousel' : ''}`}
        style={{ background: cur.bg, borderColor: cur.border }}
      >
        {/* Arrows */}
        {isCarousel && <>
          <button className="db2-carousel-arrow db2-carousel-prev" onClick={goPrev} aria-label="Précédent">‹</button>
          <button className="db2-carousel-arrow db2-carousel-next" onClick={goNext} aria-label="Suivant">›</button>
        </>}

        {/* Slides viewport */}
        <div className="db2-hmission-slides-wrap">
          <div className="db2-hmission-slides-inner" style={{ transform: `translateX(-${currentIdx * 100}%)` }}>
            {missions.map((m, idx) => (
              <div key={idx} className="db2-hmission-slide">
                {/* Text content */}
                <div className="db2-hmission-text">
                  <div className="db2-hmission-title" style={{ color: m.color }}>{m.title}</div>
                  <div className="db2-hmission-body">{m.body}</div>
                  <Link to={m.link}>
                    <button className="db2-hmission-cta" style={{ background: m.color }}>{m.cta}</button>
                  </Link>
                </div>
                {/* Contact card */}
                {m.featured && (
                  <div className="db2-mission-contact-card">
                    <div className="db2-mission-contact-avatar" style={getAvatarStyle(m.featured.first_name)}>
                      {(m.featured.first_name || '?').charAt(0)}{(m.featured.last_name || '').charAt(0)}
                    </div>
                    <div className="db2-mission-contact-name">{m.featured.first_name} {m.featured.last_name}</div>
                    {m.featured.category === 'VIP' && <span className="vip-chip" style={{ margin: '2px auto 0' }}>⭐ VIP</span>}
                    <div className="db2-mission-contact-meta">
                      {getLocalAge(m.featured.birth_date) ? `${getLocalAge(m.featured.birth_date)} ans` : ''}
                      {m.featured.city ? ` · ${m.featured.city}` : ''}
                    </div>
                    {(m.featured.notes || m.featured.relationship_memory) && (
                      <div className="db2-mission-contact-notes">
                        💬 {m.featured.notes || m.featured.relationship_memory}
                      </div>
                    )}
                    <Link to={`/contacts/${m.featured.id}`} className="db2-mission-contact-btn" style={{ background: m.color }}>
                      Voir la fiche →
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        {isCarousel && (
          <div className="db2-carousel-dots" style={{ marginTop: 10 }}>
            {missions.map((_, i) => (
              <button key={i} className={`db2-carousel-dot${i === currentIdx ? ' active' : ''}`} onClick={() => goTo(i)} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── HealthScore — accepts server score directly ────────────────────────────────
function HealthScore({ serverScore }) {
  const { analytics, contacts } = useApp();
  // crm_health from backend is {score, metrics} — extract the number
  const rawScore = serverScore ?? analytics?.crm_health ?? 0;
  const score = typeof rawScore === 'object' ? (rawScore?.score ?? 0) : (rawScore ?? 0);
  const color = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#EF4444';
  const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Bon' : 'À améliorer';

  const total = contacts.length || 1;
  // Use metrics from server if available
  const metrics = typeof rawScore === 'object' ? rawScore?.metrics : null;
  const emailRate    = metrics?.email_rate     ?? analytics?.email_rate    ?? Math.round(contacts.filter(c => c.email).length / total * 100);
  const birthdateRate= metrics?.birthdate_rate ?? analytics?.birthdate_rate ?? Math.round(contacts.filter(c => c.birth_date).length / total * 100);
  const phoneRate    = metrics?.phone_rate     ?? analytics?.phone_rate    ?? Math.round(contacts.filter(c => c.phone).length / total * 100);
  const activeRate   = metrics?.active_rate    ?? analytics?.active_rate   ?? Math.round(contacts.filter(c => c.status === 'active').length / total * 100);

  return (
    <div>
      <div className="health-score-ring">
        <svg viewBox="0 0 80 80" width="80" height="80">
          <circle cx="40" cy="40" r="32" fill="none" stroke="var(--line)" strokeWidth="6" />
          <circle cx="40" cy="40" r="32" fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={`${(score / 100) * 201} 201`} strokeLinecap="round"
            transform="rotate(-90 40 40)" />
        </svg>
        <div>
          <div className="health-score-value" style={{ color }}>{score}</div>
          <div style={{ fontSize: 10, color, fontWeight: 700 }}>{label}</div>
        </div>
      </div>
      <div className="health-metrics">
        {[
          { label: 'E-mails valides', value: emailRate,     icon: '📧' },
          { label: 'Dates naissance', value: birthdateRate, icon: '🎂' },
          { label: 'Téléphones',      value: phoneRate,     icon: '📱' },
          { label: 'Contacts actifs', value: activeRate,    icon: '✅' },
        ].map(m => (
          <div key={m.label} className="health-metric">
            <span className="health-metric-icon">{m.icon}</span>
            <div className="health-metric-bar-wrap">
              <div className="health-metric-label">{m.label}</div>
              <div className="health-metric-bar">
                <div className="health-metric-fill" style={{ width: `${m.value}%`, background: m.value >= 70 ? '#10B981' : m.value >= 50 ? '#F59E0B' : '#EF4444' }} />
              </div>
            </div>
            <span className="health-metric-pct">{m.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── UsageCard — uses /api/dashboard usage object directly ─────────────────────
function quotaColor(pct) {
  if (pct >= 100) return 'var(--danger)';
  if (pct >= 90)  return '#F97316';
  if (pct >= 80)  return '#F59E0B';
  return 'var(--violet)';
}
function quotaAppeeVoice(label, used, total, pct) {
  const remaining = total - used;
  if (pct >= 100) return `⛔ Ton quota ${label.toLowerCase()} est atteint. Les prochains envois sont suspendus.`;
  if (pct >= 90)  return `🔴 Il te reste seulement ${remaining.toLocaleString()} ${label.toLowerCase()}. Je te conseille d'ajouter un pack.`;
  if (pct >= 80)  return `🟡 Tu approches de ta limite (${remaining.toLocaleString()} restants). Surveille ta consommation.`;
  if (pct >= 70)  return `Ta consommation augmente. Il te reste ${remaining.toLocaleString()} ${label.toLowerCase()} — Appee continue normalement.`;
  return null;
}

function UsageCard({ usage, tenant }) {
  const planName = usage.plan ?? tenant?.plan ?? '';
  const planLabel = planName ? planName.charAt(0).toUpperCase() + planName.slice(1) : 'Chargement…';

  const smsCredits = usage.sms_credits ?? 0;
  const items = [
    { label: 'E-mails',        used: usage.emails_used   ?? tenant?.emails_used   ?? 0, total: usage.emails_limit   ?? tenant?.emails_limit   ?? 1000 },
    { label: 'Générations IA', used: usage.ai_used       ?? tenant?.ai_used       ?? 0, total: usage.ai_limit       ?? tenant?.ai_limit       ?? 20   },
    { label: 'Contacts',       used: usage.contacts_used ?? 0,                          total: usage.contacts_limit ?? tenant?.contacts_limit ?? 500  },
  ];

  return (
    <Card>
      <CardHeader title="📦 Utilisation du plan" subtitle={`Plan ${planLabel}`} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {items.map(item => {
          const pct   = item.total > 0 ? Math.min(Math.round((item.used / item.total) * 100), 100) : 0;
          const color = quotaColor(pct);
          const tip   = quotaAppeeVoice(item.label, item.used, item.total, pct);
          return (
            <div key={item.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span style={{ fontWeight: 600 }}>{item.label}</span>
                <span style={{ color: pct >= 80 ? color : 'var(--muted)', fontWeight: pct >= 80 ? 700 : 400 }}>
                  {item.used.toLocaleString()} / {item.total.toLocaleString()}
                </span>
              </div>
              <div style={{ height: 6, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width .5s' }} />
              </div>
              {tip && (
                <div className="appee-tip" style={{ marginTop: 7, fontSize: 11 }}>{tip}</div>
              )}
            </div>
          );
        })}

        {/* SMS credits — separate display (credits remaining, not % used) */}
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>📱 Crédits SMS</span>
            <span style={{
              fontWeight: 700, fontSize: 14,
              color: smsCredits === 0 ? '#6B7280' : smsCredits < 20 ? '#F59E0B' : '#10B981',
            }}>
              {smsCredits} crédit{smsCredits !== 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
            {smsCredits === 0
              ? '⚪ Aucun crédit SMS — module non activé'
              : smsCredits < 20
              ? `🟡 Stock faible — pensez à recharger`
              : '✅ Crédits SMS disponibles'}
          </div>
        </div>
      </div>
    </Card>
  );
}
